<template>
  <b-container class="area-item" fluid>
    <Transition name="fade">
      <div v-if="area" class="area-detail">

        <!-- Header: always-visible identity + key actions -->
        <div class="area-header">
          <div class="area-header-main">
            <h1 class="area-title">
              {{ area.name }}
              <a v-if="area.lat && area.lng" :href="'https://www.google.com/maps/place/'+area.lat+','+area.lng"
                 class="area-title-gps" target="_blank" title="Open in Google Maps">
                <i class="fa-solid fa-location-dot"></i>
              </a>
              <b-badge v-if="area.area_ha" variant="secondary" class="area-title-badge">
                {{
                  new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(area.area_ha)
                }} Ha
              </b-badge>
              <span v-if="area.tree_purpose && area.tree_purpose.trees > 0" class="purpose-pill purpose-trees" :title="purposeTitle('trees')">Trees</span>
              <span v-if="area.tree_purpose && area.tree_purpose.splits > 0" class="purpose-pill purpose-splits" :title="purposeTitle('splits')">Splits</span>
            </h1>
            <div v-if="area.country" class="area-country">
              <CountryFlag :country="area.country" /> <span>{{ area.country }}</span>
            </div>
          </div>
          <div class="area-header-stats">
            <div class="area-stat">
              <div class="area-stat-label">Wallet</div>
              <div class="area-stat-value">
                <a :href="explorerUrl+'/address/'+area.wallet_address" target="_blank">{{ textShort(area.wallet_address) }}</a>
              </div>
            </div>
            <div v-if="area.cadastre || area.cadastre_certificate" class="area-stat">
              <div class="area-stat-label">Cadastre</div>
              <div class="area-stat-value">
                <a v-if="area.url" :href="area.url" target="_blank">{{ area.cadastre }}</a>
                <span v-else>{{ area.cadastre }}</span>
                <a v-if="area.cadastre_certificate" :href="imageUrl+area.cadastre_certificate" target="_blank" title="Certificate" class="ml-1">
                  <i class="fa-solid fa-file-lines"></i>
                </a>
              </div>
            </div>
            <div class="area-stat">
              <div class="area-stat-label">Mean Density</div>
              <div class="area-stat-value">
                {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(area.qty / area.area_ha) }} Trees/Ha
              </div>
            </div>
            <div class="area-stat">
              <div class="area-stat-label">Trees</div>
              <div class="area-stat-value">
                <a href="#" role="button" title="View breakdown" @click.prevent="showTreeTypesModal = true">
                  {{ new Intl.NumberFormat("de-DE").format(area.qty) }}
                  <i class="fa-solid fa-list ml-1"></i>
                </a>
              </div>
            </div>
            <div v-if="area.tree_purpose" class="area-stat">
              <div class="area-stat-label">Stock</div>
              <div class="area-stat-value">
                <span class="purpose-pill purpose-trees" :title="purposeTitle('trees')">Trees {{ fmt(area.tree_purpose.trees) }}</span>
                <span class="purpose-pill purpose-splits" :title="purposeTitle('splits')">Splits {{ fmt(area.tree_purpose.splits) }}</span>
              </div>
            </div>
            <div class="area-stat">
              <div class="area-stat-label">Balance</div>
              <div class="area-stat-value">
                <a v-if="balance != null && config"
                   :href="explorerUrl+'/token/'+config.token_contract_address+'?a='+area.wallet_address"
                   target="_blank">{{
                    new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(balance)
                  }} ZCO2</a>
                <i v-else class="fa-solid fa-spinner fa-spin-pulse"></i>
              </div>
            </div>
            <div class="area-stat">
              <div class="area-stat-label">Owner</div>
              <div class="area-stat-value">
                {{ area.user_name || "Unassigned" }}
                <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                   class="far fa-edit fa-xs ml-1" role="button" title="Change owner" @click="changeOwner"></i>
              </div>
            </div>
            <button v-if="balance != null &&
              area.user_id && getWeb2ActiveAccount && getWeb2ActiveAccount.id === area.user_id &&
              (getWeb2ActiveAccount.role.name === 'Administrator' || getWeb2ActiveAccount.role.name === 'Authenticated')"
                    class="btn btn-primary area-withdraw-btn" @click="withdraw">
              <i class="fa-solid fa-money-bill-transfer mr-1"></i> Withdraw
            </button>
            <button v-if="isAdministrator" class="btn btn-outline-primary area-withdraw-btn" @click="openTreeStock">
              <i class="fa-solid fa-right-left mr-1"></i> Transfer trees
            </button>
          </div>
        </div>

        <b-tabs class="area-main-tabs" content-class="mt-3">
          <b-tab title="Overview" active>
            <b-row class="overview-row" ref="overviewRow">
              <b-col class="mb-3 overview-col-fill" lg="4" md="5" :style="pcFillStyle">
                <a
                  v-if="area.token_id && !area.deleted_token"
                  :href="'https://opensea.io/assets/matic/'+area.nft_smart_contract+'/'+area.token_id"
                  class="btn btn-secondary btn-block d-block d-md-none mb-2"
                  target="_blank">View NFT<i
                  class="fa-solid fa-arrow-up-right-from-square ml-1"></i></a>

                <b-card class="bg-primary p-2 mb-2 map-card-fill" no-body>
                  <div class="d-flex justify-content-between align-items-center px-2 py-1">
                    <div class="font-weight-bold small">Map</div>
                    <b-button-group size="sm">
                      <b-button :variant="mapProvider === 'mapbox' ? 'primary' : 'outline-light'"
                                @click="mapProvider = 'mapbox'">Satellite</b-button>
                      <b-button v-if="showMmlToggle" :variant="mapProvider === 'mml' ? 'primary' : 'outline-light'"
                                @click="mapProvider = 'mml'">Aerial</b-button>
                      <b-button v-if="showMmlToggle" :variant="mapProvider === 'mml-topo' ? 'primary' : 'outline-light'"
                                @click="mapProvider = 'mml-topo'">Topographic</b-button>
                      <b-button v-if="show3dMap" :variant="mapProvider === '3d' ? 'primary' : 'outline-light'"
                                @click="mapProvider = '3d'">3D</b-button>
                    </b-button-group>
                  </div>
                  <div class="map-box map-box-lg">
                    <Transition v-if="mapProvider !== '3d' && area.last_checkpoint?.data != undefined" name="fade">
                      <MapBoxItemComponent :areaData="area.last_checkpoint.data" :provider="mapProvider" />
                    </Transition>
                    <PointCloud3DComponent v-if="mapProvider === '3d'" map-id="point-cloud-inline" />
                    <div v-if="mapProvider !== '3d' && area.last_checkpoint?.data == undefined" style="background: #2e4032;"
                         class="card h-100 justify-content-center align-items-center">
                      <b-spinner></b-spinner>
                    </div>
                    <button v-if="mapProvider === '3d' || area.last_checkpoint?.data != undefined" type="button"
                            class="map-expand-btn" title="Expand map" @click="showMapModal = true">
                      <i class="fa-solid fa-expand"></i>
                    </button>
                  </div>
                </b-card>
              </b-col>

              <b-col class="mb-2 overview-col-fill" lg="8" md="7" :style="pcFillStyle">
                <div v-if="!theoreticalFormulaStats.length" class="card growth-panel-loading justify-content-center align-items-center mb-3"
                     style="background: #2e4032;">
                  <b-spinner></b-spinner>
                </div>
                <b-card v-else class="bg-primary px-3 pt-2 pb-2 mb-2 chart-card-fill" no-body>
                  <b-tabs content-class="mt-1" @input="scheduleTableHeightUpdate">
                    <b-tab title="Chart" active>
                      <div class="chart-with-side">
                        <div class="growth-chart-wrap" ref="growthChartWrapEl">
                          <button v-if="latestScan" type="button" class="scan-info-btn" :style="scanBtnStyle"
                                  title="View scan details" @click="showScanModal = true">
                            Scanned {{ latestScan.date }}
                            <i class="fa-solid fa-arrow-up-right-from-square ml-2"></i>
                          </button>
                          <line-chart v-if="timeFormulaChartData != undefined" ref="timeChart" :chart-data="timeFormulaChartData"
                                      :options="timeFormulaChartOptions" :styles="myStyles"></line-chart>
                        </div>
                        <div class="additionality-strip additionality-side">
                          <div class="as-group">
                            <div class="as-group-header">
                              <span class="as-label">Additionality</span>
                              <button v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                      type="button" class="as-add-btn" title="Add additionality" @click="addAdditionality">
                                <i class="fa-solid fa-plus"></i> Add
                              </button>
                            </div>
                            <span v-if="!area.additionalities.length" class="text-muted small as-empty">No additionalities registered yet.</span>
                            <div v-else class="as-chip-scroll">
                              <div v-for="group in groupedAdditionalities" :key="'adgrp1'+group.type" class="as-chip-group">
                                <span class="as-chip">
                                  <span class="as-chip-icon"><i class="fa-solid fa-leaf"></i></span>
                                  <span class="as-chip-body">
                                    <span class="as-chip-title">{{ group.type }}</span>
                                  </span>
                                  <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator' && group.items.length === 1 && !group.items[0].treeType"
                                     @click="delAdditionality(group.items[0].index)" role="button" class="fa-solid fa-xmark as-chip-remove"></i>
                                </span>
                                <div v-if="group.items.some(it => it.treeType)" class="as-subitem-list">
                                  <span v-for="item in group.items" :key="'adsub1'+item.index" class="as-subitem">
                                    {{ item.treeType }}
                                    <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                       @click="delAdditionality(item.index)" role="button" class="fa-solid fa-xmark as-subitem-remove"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="as-group">
                            <div class="as-group-header">
                              <span class="as-label">Emissions
                                <i class="fas fa-info-circle ml-1" v-b-tooltip.hover
                                   title="ISO 14064-2: emissions caused by forestry work subtract from additionality."></i>
                              </span>
                              <button v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                      type="button" class="as-add-btn" title="Add emission" @click="openEmissionModal">
                                <i class="fa-solid fa-plus"></i> Add
                              </button>
                            </div>
                            <span v-if="!area.emissions || !area.emissions.length" class="text-muted small as-empty">No emissions logged for this forest.</span>
                            <div v-else class="as-chip-scroll">
                              <span v-for="emission in area.emissions" :key="'emchip1'+emission.id" class="as-chip as-chip--negative">
                                <span class="as-chip-icon"><i class="fa-solid fa-smog"></i></span>
                                <span class="as-chip-body">
                                  <span class="as-chip-title">{{ emission.concept }}</span>
                                  <span class="as-chip-sub as-chip-sub--negative">-{{ emission.ton_co2 }} t CO2</span>
                                </span>
                                <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                   @click="deleteEmission(emission)" role="button" class="fa-solid fa-xmark as-chip-remove"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </b-tab>
                    <b-tab title="Theoretical (ex-ante)">
                      <b-tabs pills small :value="additionalTabsDefaultIndex" content-class="mt-2" @input="scheduleTableHeightUpdate">
                        <b-tab title="Additional">
                          <div class="chart-with-side">
                            <div class="flex-grow-1 min-w-0 table-panel">
                              <div class="mai-card mb-2">
                                <div class="mai-label">Mean Annual Increase <span class="mai-scope">(Additional)</span></div>
                                <div class="mai-value">
                                  {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(currentYearTheoreticalAdditionalIncrement / area.area_ha) }}
                                  <span class="cco2-unit">t CO2 / Year Ha</span>
                                </div>
                              </div>
                              <b-table ref="additionalTableEl" :fields="additionalFormulaStatsFields" :items="theoreticalFormulaStats"
                                       :tbody-tr-class="currentYearRowClass"
                                       :sticky-header="additionalTableHeight" responsive small class="compact-table table-fill">
                                <template #head(index)="scope"><div class="text-right">{{ scope.label }}</div></template>
                                <template #head(year)="scope"><div class="text-right">{{ scope.label }}</div></template>
                                <template #head(totalAdditionalTonCCO2)="scope"><div class="text-right">{{ scope.label }}</div></template>
                                <template #head(theoreticalAdditionalIncrement)="scope"><div class="text-right">{{ scope.label }}</div></template>
                                <template #head(emissionsTonCo2)="scope"><div class="text-right">{{ scope.label }}</div></template>
                                <template #cell(index)="data"><div class="text-right">{{ data.item.index }}</div></template>
                                <template #cell(year)="data"><div class="text-right">{{ data.item.year }}</div></template>
                                <template #cell(totalAdditionalTonCCO2)="data">
                                  <div class="text-right">
                                    {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.totalAdditionalTonCCO2) }} <span class="cco2-unit">t CO2</span>
                                  </div>
                                </template>
                                <template #cell(theoreticalAdditionalIncrement)="data">
                                  <div class="text-right">
                                    {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.theoreticalAdditionalIncrement) }} <span class="cco2-unit">t CO2</span>
                                  </div>
                                </template>
                                <template #cell(emissionsTonCo2)="data">
                                  <div class="text-right">
                                    <template v-if="data.item.emissionsTonCo2">-{{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.emissionsTonCo2) }} <span class="cco2-unit">t CO2</span></template>
                                    <template v-else>—</template>
                                  </div>
                                </template>
                              </b-table>
                            </div>
                            <div class="additionality-strip additionality-side">
                              <div class="as-group">
                                <div class="as-group-header">
                                  <span class="as-label">Additionality</span>
                                  <button v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                          type="button" class="as-add-btn" title="Add additionality" @click="addAdditionality">
                                    <i class="fa-solid fa-plus"></i> Add
                                  </button>
                                </div>
                                <span v-if="!area.additionalities.length" class="text-muted small as-empty">No additionalities registered yet.</span>
                                <div v-else class="as-chip-scroll">
                                  <div v-for="group in groupedAdditionalities" :key="'adgrp2'+group.type" class="as-chip-group">
                                    <span class="as-chip">
                                      <span class="as-chip-icon"><i class="fa-solid fa-leaf"></i></span>
                                      <span class="as-chip-body">
                                        <span class="as-chip-title">{{ group.type }}</span>
                                      </span>
                                      <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator' && group.items.length === 1 && !group.items[0].treeType"
                                         @click="delAdditionality(group.items[0].index)" role="button" class="fa-solid fa-xmark as-chip-remove"></i>
                                    </span>
                                    <div v-if="group.items.some(it => it.treeType)" class="as-subitem-list">
                                      <span v-for="item in group.items" :key="'adsub2'+item.index" class="as-subitem">
                                        {{ item.treeType }}
                                        <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                           @click="delAdditionality(item.index)" role="button" class="fa-solid fa-xmark as-subitem-remove"></i>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="as-group">
                                <div class="as-group-header">
                                  <span class="as-label">Emissions
                                    <i class="fas fa-info-circle ml-1" v-b-tooltip.hover
                                       title="ISO 14064-2: emissions caused by forestry work subtract from additionality."></i>
                                  </span>
                                  <button v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                          type="button" class="as-add-btn" title="Add emission" @click="openEmissionModal">
                                    <i class="fa-solid fa-plus"></i> Add
                                  </button>
                                </div>
                                <span v-if="!area.emissions || !area.emissions.length" class="text-muted small as-empty">No emissions logged for this forest.</span>
                                <div v-else class="as-chip-scroll">
                                  <span v-for="emission in area.emissions" :key="'emchip2'+emission.id" class="as-chip as-chip--negative">
                                    <span class="as-chip-icon"><i class="fa-solid fa-smog"></i></span>
                                    <span class="as-chip-body">
                                      <span class="as-chip-title">{{ emission.concept }}</span>
                                      <span class="as-chip-sub as-chip-sub--negative">-{{ emission.ton_co2 }} t CO2</span>
                                    </span>
                                    <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                                       @click="deleteEmission(emission)" role="button" class="fa-solid fa-xmark as-chip-remove"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </b-tab>
                        <b-tab title="Base">
                          <div class="table-panel">
                            <div class="mai-card mb-2">
                              <div class="mai-label">Mean Annual Increase <span class="mai-scope">(Base)</span></div>
                              <div class="mai-value">
                                {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(currentYearTheoreticalIncrement / area.area_ha) }}
                                <span class="cco2-unit">t CO2 / Year Ha</span>
                              </div>
                            </div>
                            <b-table ref="baseTableEl" :fields="baseFormulaStatsFields" :items="theoreticalFormulaStats"
                                     :tbody-tr-class="currentYearRowClass"
                                     :sticky-header="baseTableHeight" responsive small class="compact-table table-fill">
                              <template #head(index)="scope"><div class="text-right">{{ scope.label }}</div></template>
                              <template #head(year)="scope"><div class="text-right">{{ scope.label }}</div></template>
                              <template #head(totalTonCCO2)="scope">
                                <div class="text-right">
                                  {{ scope.label }}
                                  <i class="fas fa-info-circle ml-1" v-b-tooltip.hover title="Only trunk biomass"></i>
                                </div>
                              </template>
                              <template #head(theoreticalIncrement)="scope"><div class="text-right">{{ scope.label }}</div></template>
                              <template #cell(index)="data"><div class="text-right">{{ data.item.index }}</div></template>
                              <template #cell(year)="data"><div class="text-right">{{ data.item.year }}</div></template>
                              <template #cell(totalTonCCO2)="data">
                                <div class="text-right">
                                  {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.baseline_value &lt; 0 ? 0 : data.item.totalTonCCO2) }} <span class="cco2-unit">t CO2</span>
                                </div>
                              </template>
                              <template #cell(theoreticalIncrement)="data">
                                <div class="text-right">
                                  {{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.baseline_value &lt; 0 ? 0 : data.item.theoreticalIncrement) }} <span class="cco2-unit">t CO2</span>
                                </div>
                              </template>
                            </b-table>
                          </div>
                        </b-tab>
                      </b-tabs>
                    </b-tab>

                    <b-tab title="Real (ex-post)">
                      <div v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
                           class="d-flex justify-content-end mb-2">
                        <b-dropdown right text="Options" size="sm">
                          <b-dropdown-item @click="createNewToken()">Create NFT</b-dropdown-item>
                          <b-dropdown-item v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.id === 1"
                                           @click="createNftTokenFromCurrentYear()">Create NFT from this year</b-dropdown-item>
                          <b-dropdown-item v-if="area.token_id && !area.deleted_token"
                                           variant="danger" @click="deleteToken">Delete NFT</b-dropdown-item>
                          <b-dropdown-item :disabled="area.token_id && !area.deleted_token"
                                           @click="showCheckpointModal">Import data</b-dropdown-item>
                        </b-dropdown>
                      </div>
                      <div v-if="!practicalStats.length" class="card justify-content-center align-items-center"
                           style="background: #2e4032; min-height: 200px;">
                        <b-spinner></b-spinner>
                      </div>
                      <b-table v-else :fields="practicalStatsFields" :items="practicalStats"
                               sticky-header="240px" responsive small class="compact-table">
                    <template #head(date)="scope"><div class="text-center">{{ scope.label }}</div></template>
                    <template #head(avgDiameter)="scope"><div class="text-right" style="min-width: 100px;">{{ scope.label }}</div></template>
                    <template #head(avgHeight)="scope"><div class="text-right" style="min-width: 100px;">{{ scope.label }}</div></template>
                    <template #head(tonCCO2)="scope">
                      <div class="text-right" style="min-width: 100px;">
                        {{ scope.label }}
                        <i class="fas fa-info-circle ml-1" v-b-tooltip.hover title="Total biomass: trunk + branches + leaves + roots"></i>
                      </div>
                    </template>
                    <template #head(treeQty)="scope"><div class="text-right" style="min-width: 100px;">{{ scope.label }}</div></template>
                    <template #cell(date)="data">
                      <div class="text-center">
                        <p class="mb-0">{{ data.item.date }}</p>
                        <button v-if="data.item.contractAddress && data.item.tokenId"
                                v-b-modal="'nft-modal-'+data.item.tokenId"
                                class="btn btn-primary btn-sm mt-1">
                          NFT<i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
                        </button>
                        <b-modal :id="'nft-modal-'+data.item.tokenId" size="lg">
                          <template #modal-title>
                            <h3 class="d-inline">NFT INFO</h3>
                            <h4 v-if="!data.item.deletedToken" class="badge badge-success d-inline-block mb-0 ml-2">Active</h4>
                            <h4 v-if="data.item.deletedToken" class="badge badge-danger d-inline-block mb-0 ml-2">Inactive</h4>
                          </template>
                          <div class="text-center">
                            <b-tabs content-class="mt-3" justified>
                              <b-tab active title="Information">
                                <div class="row">
                                  <div class="col-md-5">
                                    <img v-if="area.image" :alt="data.item.name" :src="imageUrl+area.image" class="w-100 mb-3">
                                  </div>
                                  <div class="col-md-7">
                                    <p class="font-weight-bold mb-0">Name</p>
                                    <p>{{ data.item.name }}</p>
                                    <p class="font-weight-bold mb-0">Chain</p>
                                    <p>{{ data.item.chain }}</p>
                                    <p class="font-weight-bold mb-0">Smart Contract</p>
                                    <p>{{ data.item.contractAddress }}</p>
                                    <p class="font-weight-bold mb-0">Token ID {{ data.item.tokenId }}</p>
                                    <a :href="data.item.tokenIdUrl" class="mb-3 d-block" target="_blank">
                                      View on Explorer<i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
                                    </a>
                                    <div v-if="!data.item.deletedToken">
                                      <p class="font-weight-bold mb-0">OpenSea</p>
                                      <a :href="'https://opensea.io/assets/matic/'+data.item.contractAddress+'/'+data.item.tokenId" target="_blank">
                                        View on OpenSea<i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </b-tab>
                              <b-tab title="Blockchain Stats">
                                <b-table :items="blockchainTheoreticalStats" head-variant="dark"></b-table>
                              </b-tab>
                            </b-tabs>
                          </div>
                          <template #modal-footer="{ ok }">
                            <b-button class="mx-auto" variant="primary" @click="ok()">OK</b-button>
                          </template>
                        </b-modal>
                      </div>
                    </template>
                    <template #cell(avgDiameter)="data">
                      <div v-for="(treeType, i) of data.item.treeTypes" :key="'B'+i" class="text-right">
                        <b>{{ treeType.name }}:</b> {{
                          new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.diameterAverage || 0)
                        }}
                      </div>
                    </template>
                    <template #cell(avgHeight)="data">
                      <div v-for="(treeType, i) of data.item.treeTypes" :key="'C'+i" class="text-right">
                        <b>{{ treeType.name }}:</b> {{
                          new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.heightAverage || 0)
                        }}
                      </div>
                    </template>
                    <template #cell(tonCCO2)="data">
                      <div v-for="(treeType, i) of data.item.treeTypes" :key="'D'+i" class="text-right">
                        <b>{{ treeType.name }}:</b> {{
                          new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.CO2 || 0)
                        }}
                      </div>
                      <div class="total text-right font-weight-bold">
                        Total: {{
                          new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.item.totalTonCCO2)
                        }}
                      </div>
                    </template>
                    <template #cell(treeQty)="data">
                      <div v-for="(treeType, i) of data.item.treeTypes" :key="'E'+i" class="text-right">
                        <b>{{ treeType.name }}:</b>
                        {{ new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(treeType.qty || 0) }}
                      </div>
                      <div class="total text-right font-weight-bold">
                        Total: {{ new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(data.item.totalTreeQty) }}
                      </div>
                    </template>
                  </b-table>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="Management Plan & ISO Compliance">
            <ManagementPlanTab
              :area-id="area.id"
              :contract="area.forest_management_contract"
              :tasks="area.forest_tasks"
              :responsible="area.iso_responsible"
              @reload="loadArea" />

            <b-card class="certificates-card mt-3" no-body>
              <div class="certificates-title">Certificates</div>
              <div class="certificates-list">
                <div v-if="area.cadastre || area.cadastre_certificate" class="cert-row">
                  <i class="fas fa-map-location-dot cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Cadastre</div>
                    <div class="cert-value">
                      <a v-if="area.url" :href="area.url" target="_blank">{{ area.cadastre }}</a>
                      <span v-else>{{ area.cadastre }}</span>
                    </div>
                  </div>
                  <a v-if="area.cadastre_certificate" :href="imageUrl+area.cadastre_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.insurance_certificate" class="cert-row">
                  <i class="fas fa-file-shield cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Insurance</div>
                  </div>
                  <a :href="imageUrl+area.insurance_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.fertilizing_certificate" class="cert-row">
                  <i class="fas fa-seedling cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Fertilizing</div>
                  </div>
                  <a :href="imageUrl+area.fertilizing_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.needle_analyze" class="cert-row">
                  <i class="fas fa-magnifying-glass-chart cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Needle analyze</div>
                  </div>
                  <a :href="imageUrl+area.needle_analyze" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="!area.cadastre && !area.cadastre_certificate && !area.insurance_certificate && !area.fertilizing_certificate && !area.needle_analyze && !area.additionality_certificate"
                     class="text-muted small py-2">No certificates uploaded.</div>
              </div>

              <div v-if="area.additionality_certificate" class="cert-badge-wrap">
                <a :href="imageUrl+area.additionality_certificate" target="_blank" title="View full size">
                  <img :src="imageUrl+area.additionality_certificate" alt="ISO compliance badge" class="cert-badge-img">
                </a>
              </div>
            </b-card>
          </b-tab>

          <b-tab title="Certificates">
            <b-card class="certificates-card" no-body>
              <div class="certificates-list">
                <div v-if="area.cadastre || area.cadastre_certificate" class="cert-row">
                  <i class="fas fa-map-location-dot cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Cadastre</div>
                    <div class="cert-value">
                      <a v-if="area.url" :href="area.url" target="_blank">{{ area.cadastre }}</a>
                      <span v-else>{{ area.cadastre }}</span>
                    </div>
                  </div>
                  <a v-if="area.cadastre_certificate" :href="imageUrl+area.cadastre_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.insurance_certificate" class="cert-row">
                  <i class="fas fa-file-shield cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Insurance</div>
                  </div>
                  <a :href="imageUrl+area.insurance_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.fertilizing_certificate" class="cert-row">
                  <i class="fas fa-seedling cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Fertilizing</div>
                  </div>
                  <a :href="imageUrl+area.fertilizing_certificate" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="area.needle_analyze" class="cert-row">
                  <i class="fas fa-magnifying-glass-chart cert-icon"></i>
                  <div class="cert-info">
                    <div class="cert-label">Needle analyze</div>
                  </div>
                  <a :href="imageUrl+area.needle_analyze" class="cert-link" target="_blank">
                    <i class="fas fa-file-arrow-down mr-1"></i>View
                  </a>
                </div>

                <div v-if="!area.cadastre && !area.cadastre_certificate && !area.insurance_certificate && !area.fertilizing_certificate && !area.needle_analyze && !area.additionality_certificate"
                     class="text-muted small py-2">No certificates uploaded.</div>
              </div>

              <div v-if="area.additionality_certificate" class="cert-badge-wrap">
                <a :href="imageUrl+area.additionality_certificate" target="_blank" title="View full size">
                  <img :src="imageUrl+area.additionality_certificate" alt="ISO compliance badge" class="cert-badge-img">
                </a>
              </div>
            </b-card>
          </b-tab>

          <b-tab title="More Information">
            <b-card class="bg-primary px-3 py-2" no-body>
              <div v-if="area.identification_code" class="mb-2">
                <div class="font-weight-bold">Identification code</div>
                <a v-if="area.contract" :href="imageUrl+area.contract" class="text-white" target="_blank">{{ area.identification_code }}</a>
                <span v-else>{{ area.identification_code }}</span>
              </div>
              <div v-if="area.additionality_type" class="mb-2">
                <div class="font-weight-bold">Type of additionality</div>
                {{ area.additionality_type }}
              </div>
              <div v-if="area.project_duration" class="mb-2">
                <div class="font-weight-bold">Project duration</div>
                {{ area.project_duration }} years
              </div>
              <div v-if="!area.identification_code && !area.additionality_type && !area.project_duration"
                   class="text-muted small">No additional details.</div>
            </b-card>
          </b-tab>
        </b-tabs>

        <!-- Map expand modal -->
        <b-modal v-model="showMapModal" size="xl" hide-footer centered body-class="p-0" title="Forest map">
          <div class="map-modal-box">
            <MapBoxItemComponent v-if="showMapModal && mapProvider !== '3d' && area.last_checkpoint?.data != undefined"
                                  :areaData="area.last_checkpoint.data" :provider="mapProvider" map-id="map-modal" />
            <PointCloud3DComponent v-if="showMapModal && mapProvider === '3d'" map-id="point-cloud-modal" />
          </div>
        </b-modal>

        <!-- Tree types breakdown modal -->
        <b-modal v-model="showTreeTypesModal" title="Tree Types Breakdown" hide-footer>
          <div v-for="(treeType, i) of area.treeTypes" :key="'TT'+treeType.id+'T'+treeType.qty" class="mb-2">
            {{ treeType.name }}
            <i v-if="getWeb2ActiveAccount && getWeb2ActiveAccount.role.name === 'Administrator'"
               class="far fa-edit fa-xs" role="button" @click="changeTreeType(i)"></i>
            <b-badge :style="{ backgroundColor: treeType.color }" class="float-right">
              {{ new Intl.NumberFormat("de-DE").format(treeType.qty) }}
            </b-badge>
          </div>
        </b-modal>

        <!-- Tree stock (administrators): what the trees of the forest are for, and moving free trees between Trees and Splits -->
        <b-modal v-model="showTreeStockModal" title="Tree stock" size="lg" hide-footer centered>
          <div v-if="treeStockLoading" class="text-center py-4"><i class="fa-solid fa-spinner fa-spin-pulse"></i></div>
          <div v-else-if="treeStock">
            <p class="text-muted small mb-3">
              Every tree of <strong>{{ area.name }}</strong> is meant for <strong>Trees</strong> (sold whole, one ERC-721 token) or for
              <strong>Splits</strong> (shares of the tree, ERC-1155). Only <em>free</em> trees can change: not minted, no splits delivered
              and not assigned to a company.
            </p>
            <b-row class="mb-3">
              <b-col md="6" class="mb-2">
                <div class="purpose-card">
                  <div class="purpose-card-title"><span class="purpose-pill purpose-trees">Trees</span> whole trees</div>
                  <div class="purpose-card-total">{{ fmt(treeStock.trees.total) }}</div>
                  <ul class="purpose-card-list">
                    <li><strong>{{ fmt(treeStock.trees.free) }}</strong> free (can move to Splits)</li>
                    <li><strong>{{ fmt(treeStock.trees.used) }}</strong> minted</li>
                  </ul>
                </div>
              </b-col>
              <b-col md="6" class="mb-2">
                <div class="purpose-card">
                  <div class="purpose-card-title"><span class="purpose-pill purpose-splits">Splits</span> {{ fmt(treeStock.splitsPerTree) }} splits per tree</div>
                  <div class="purpose-card-total">{{ fmt(treeStock.splits.total) }}</div>
                  <ul class="purpose-card-list">
                    <li><strong>{{ fmt(treeStock.splits.free) }}</strong> free (can move to Trees)</li>
                    <li><strong>{{ fmt(treeStock.splits.assigned) }}</strong> assigned to companies</li>
                    <li><strong>{{ fmt(treeStock.splits.used) }}</strong> in use (minting or delivered)</li>
                    <li><strong>{{ fmt(treeStock.splits.splitsAvailable) }}</strong> splits still available</li>
                  </ul>
                </div>
              </b-col>
            </b-row>

            <div class="purpose-form">
              <div class="font-weight-bold mb-2">Transfer free trees</div>
              <b-form-radio-group v-model="treeMove.to" buttons button-variant="outline-primary" size="sm" class="mb-2"
                                   :options="[{ text: 'To Trees', value: 'trees' }, { text: 'To Splits', value: 'splits' }]"></b-form-radio-group>
              <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                <b-form-input v-model.number="treeMove.count" type="number" min="1" :max="treeMovable" :disabled="!treeMovable" style="max-width: 140px;"></b-form-input>
                <span class="text-muted small">of {{ fmt(treeMovable) }} free {{ treeMove.to === 'trees' ? 'Splits' : 'Trees' }} trees</span>
                <b-button variant="primary" :disabled="!treeMoveValid || treeMoving" @click="confirmTreeMove">
                  <i class="fa-solid fa-right-left mr-1"></i>{{ treeMoving ? "Transferring…" : "Transfer" }}
                </b-button>
              </div>
              <div v-if="!treeMovable" class="text-muted small mt-2">
                There are no free {{ treeMove.to === "trees" ? "Splits" : "Trees" }} trees in this forest to move.
              </div>
              <div v-if="treeStockError" class="text-danger small mt-2">{{ treeStockError }}</div>
            </div>

            <div v-if="treeStock.history && treeStock.history.length" class="mt-3">
              <div class="font-weight-bold small mb-1">Latest transfers</div>
              <div v-for="h in treeStock.history" :key="h.id" class="small text-muted">
                {{ fmt(h.count) }} tree{{ h.count === 1 ? "" : "s" }} to {{ h.to === "trees" ? "Trees" : "Splits" }}
                · {{ h.performedBy || "—" }} · {{ h.at ? new Date(h.at).toLocaleString() : "" }}
              </div>
            </div>
          </div>
          <div v-else class="text-danger">{{ treeStockError || "Could not load the stock." }}</div>
        </b-modal>

        <!-- Scan (Real ex-post) detail modal -->
        <b-modal v-model="showScanModal" title="Forest Scan Details" size="lg" hide-footer>
          <div v-if="latestScan">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <p class="text-muted mb-0">Scanned on {{ latestScan.date }}</p>
              <button v-if="latestScan.contractAddress && latestScan.tokenId"
                      v-b-modal="'nft-modal-'+latestScan.tokenId"
                      class="btn btn-primary btn-sm">
                View NFT<i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
              </button>
            </div>
            <div class="scan-detail-grid">
              <div v-for="(treeType, i) of latestScan.treeTypes" :key="'scan'+i" class="scan-detail-card">
                <h5>{{ treeType.name }}</h5>
                <div class="scan-detail-row"><span>Avg Diameter</span><strong>{{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.diameterAverage || 0) }} cm</strong></div>
                <div class="scan-detail-row"><span>Avg Height</span><strong>{{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.heightAverage || 0) }} m</strong></div>
                <div class="scan-detail-row"><span>CO2</span><strong>{{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(treeType.CO2 || 0) }} t</strong></div>
                <div class="scan-detail-row"><span>Trees</span><strong>{{ new Intl.NumberFormat("de-DE").format(treeType.qty || 0) }}</strong></div>
              </div>
            </div>
            <div class="scan-detail-total">
              <span>Total CO2: <strong>{{ new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(latestScan.totalTonCCO2) }} t</strong></span>
              <span>Total Trees: <strong>{{ new Intl.NumberFormat("de-DE").format(latestScan.totalTreeQty) }}</strong></span>
            </div>
          </div>
        </b-modal>

        <!-- Add emission modal -->
        <b-modal v-model="emissionModal.show" title="Add Emission" @ok="saveEmission">
          <!-- typed (day/month/year) or picked from a calendar with month and year dropdowns: the old calendar
               dropdown could only go back one year per click -->
          <b-form-group label="Date" label-for="emission-date">
            <date-field id="emission-date" v-model="emissionModal.date"/>
          </b-form-group>
          <b-form-group label="Concept">
            <b-form-input v-model="emissionModal.concept" placeholder="e.g. Fuel for site clearing"></b-form-input>
          </b-form-group>
          <b-form-group label="Ton CO2">
            <b-form-input v-model="emissionModal.tonCo2" type="number" step="0.01"></b-form-input>
          </b-form-group>
          <b-form-group label="Notes">
            <b-form-textarea v-model="emissionModal.notes"></b-form-textarea>
          </b-form-group>
        </b-modal>

        <CheckpointModalComponent v-if="area && area.id" ref="checkpointModal" :area="area" @result="loadArea" />
      </div>
    </Transition>
    <div v-if="!area" class="card area-loading justify-content-center align-items-center"
         style="background: #2e4032;height: calc(100vh - 65px);">
      <svg class="plant-grow" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
        <ellipse class="plant-soil" cx="50" cy="112" rx="26" ry="5"></ellipse>
        <g class="plant-sway">
          <path class="plant-stem" d="M50,110 Q46,86 50,60"></path>
          <path class="plant-leaf plant-leaf-left"
                d="M50,66 C37,64 26,55 28,43 C41,45 50,54 50,66 Z"></path>
          <path class="plant-leaf plant-leaf-right"
                d="M50,66 C63,64 74,55 72,43 C59,45 50,54 50,66 Z"></path>
          <circle class="plant-bud" cx="50" cy="58" r="3.2"></circle>
        </g>
      </svg>
      <p class="area-loading-text">Growing your forest view&hellip;</p>
    </div>
  </b-container>
</template>

<script>
import MapBoxItemComponent from "../../components/Area/MapBoxItemComponent";
import PointCloud3DComponent from "../../components/Area/PointCloud3DComponent";
import Area from "../../api/Area";
import LineChart from "../../components/Chart/LineChart.js";
import moment from "moment";
// import Ajax from "../../api/Ajax";
import Swal from "sweetalert2";
import TreeType from "../../api/TreeType";
import { mapActions, mapGetters } from "vuex";
import Checkpoint from "../../api/Checkpoint";
// import {create, all} from 'mathjs'
import { textShortener } from "../../utils/textShortener";
import { renderTreeTypeData } from "../../utils/renderTreeTypeData";
import User from "../../api/User";
import Config from "../../api/Config";
import CheckpointModalComponent from "../../components/Checkpoints/CheckpointModalComponent";
// import CheckpointTreeTypes from "../../api/Checkpoint";
import Additionalities from "@/api/Additionality";
import Fertilizings from "@/api/Fertilizing";
import Emission from "@/api/Emission";
import DateField from "@/components/structure/DateField.vue";
import CountryFlag from "../../components/Area/CountryFlag";
import ManagementPlanTab from "../../components/Area/ManagementPlanTab";
import createPageTourMixin from "../../mixins/pageTour";
import { maybeAutoRunTour } from "../../utils/tour";

const tourSteps = [
  {
    element: ".area-header-stats",
    popover: {
      title: "Forest overview",
      description: "Key numbers at a glance: wallet, mean tree density, tree count, ZCO2 balance and the forest's owner."
    }
  },
  {
    element: ".map-card-fill",
    popover: {
      title: "Interactive map",
      description: "The forest's boundary. Switch between satellite, aerial and topographic views, or expand it full-screen with the icon in the corner."
    }
  },
  {
    element: ".growth-chart-wrap",
    popover: {
      title: "CO2 growth chart",
      description: "Watch how this forest's CO2 accumulates over time. Use the tabs above to switch to the theoretical projection or the real, scan-based data."
    }
  },
  {
    element: ".scan-info-btn",
    popover: {
      title: "Latest scan",
      description: "Click here anytime to see a full breakdown of the most recent ground scan."
    }
  },
  {
    element: ".additionality-side",
    popover: {
      title: "Additionality & emissions",
      description: "Extra practices (like postponed felling) that add to this forest's credited CO2, and any emissions that subtract from it."
    }
  },
  {
    element: ".area-main-tabs",
    popover: {
      title: "More about this forest",
      description: "Certificates, the management plan & ISO compliance checklist, and any other details all live in these tabs."
    }
  }
];

export default {
  name: "AreaItem",

  mixins: [createPageTourMixin('area-item', tourSteps)],

  components: {
    MapBoxItemComponent,
    PointCloud3DComponent,
    CheckpointModalComponent,
    LineChart,
    CountryFlag,
    ManagementPlanTab,
    DateField
  },
  computed: {
    ...mapGetters("user", ["isWeb2UserConnected", "getWeb2ActiveAccount"]),
    ...mapGetters("tokenContract", ["getTokenBalance"]),
    ...mapGetters("accounts", ["getActiveAccount"]),
    myStyles() {
      return {
        height: "100%",
        position: "relative"
      };
    },
    isAdministrator() {
      return !!(this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role && this.getWeb2ActiveAccount.role.name === "Administrator");
    },
    // Free trees that can move to the chosen destination (they come from the other kind)
    treeMovable() {
      if (!this.treeStock) return 0;
      return this.treeMove.to === "trees" ? this.treeStock.splits.free : this.treeStock.trees.free;
    },
    treeMoveValid() {
      const n = this.treeMove.count;
      return Number.isInteger(n) && n >= 1 && n <= this.treeMovable;
    },
    showMmlToggle() {
      return !!(this.area && this.area.country === "Finland" && process.env.VUE_APP_MML_API_KEY);
    },
    // Collapses repeated additionality rows of the same type (e.g. "Wood ash
    // fertilizing" logged once per tree species) into a single chip with a
    // small per-tree-type sub-list underneath, instead of repeating the same
    // title N times. Keeps each item's original index into
    // area.additionalities so delAdditionality(i) still works unchanged.
    groupedAdditionalities() {
      const groups = [];
      const byType = {};
      (this.area.additionalities || []).forEach((a, i) => {
        if (!byType[a.type]) {
          byType[a.type] = { type: a.type, items: [] };
          groups.push(byType[a.type]);
        }
        byType[a.type].items.push({ treeType: a.tree_type, index: i });
      });
      return groups;
    },
    additionalTabsDefaultIndex() {
      const hasAdditionalityValues = this.area && this.area.additionalities &&
        this.area.additionalities.some(a => a.values && a.values.length);
      return hasAdditionalityValues ? 0 : 1;
    },
    latestScan() {
      // practicalStats is built newest-first (reversed) in renderStatsTable().
      return this.practicalStats.length ? this.practicalStats[0] : null;
    }
  },

  data() {
    return {
      apiUrl: process.env.VUE_APP_API_URL,
      imageUrl: process.env.VUE_APP_IMAGE_URL,
      explorerUrl: process.env.VUE_APP_EXPLORER_URL,
      NFTModalTabIndex: 1,
      balance: null,
      config: null,
      area: null,
      nft: false,
      showMapModal: false,
      showTreeTypesModal: false,
      showScanModal: false,
      showTreeStockModal: false,
      treeStock: null,
      treeStockLoading: false,
      treeStockError: "",
      treeMove: { to: "trees", count: 1 },
      treeMoving: false,
      mapProvider: "mapbox",
      // 3D tab hidden for now (2026-10-23): Cloud Run refuses to send the ~90 MB demo point cloud in one response
      // (32 MiB limit), so the 3D view cannot load it; to be looked at again.
      show3dMap: false,
      emissionModal: { show: false, date: null, concept: "", tonCo2: null, notes: "" },
      baseFormulaStatsFields: [
        { key: "index", label: "Index" },
        { key: "year", label: "Farming Year" },
        { key: "totalTonCCO2", label: "Accumulated" },
        { key: "theoreticalIncrement", label: "Yearly Increment" }
      ],
      additionalFormulaStatsFields: [
        { key: "index", label: "Index" },
        { key: "year", label: "Farming Year" },
        { key: "totalAdditionalTonCCO2", label: "Accumulated" },
        { key: "theoreticalAdditionalIncrement", label: "Yearly Increment" },
        { key: "emissionsTonCo2", label: "Emissions" }
      ],
      currentYearTheoreticalIncrement: null,
      currentYearTheoreticalAdditionalIncrement: null,
      allTreeTypes: [],
      users: [],
      areaTreeTypes: [],
      blockchainTheoreticalStats: [],
      theoreticalFormulaStats: [],
      theoreticalFormulaStatsFields: [
        {
          key: "index",
          label: "Index"
        },
        {
          key: "year",
          label: "Farming Year"
        },
        {
          key: "totalTonCCO2",
          label: "Ton CCO2 Acum. (1)"
        },
        {
          key: "theoreticalIncrement",
          label: "Ton CCO2 Yearly Incr."
        },
        {
          key: "totalAdditionalTonCCO2",
          label: "Ton CCO2 Additionaly Acum."
        },
        {
          key: "theoreticalAdditionalIncrement",
          label: "Ton CCO2 Additionaly Yearly Incr."
        }
      ],
      practicalStats: [],
      practicalStatsFields: [
        {
          key: "date",
          label: "Date"
        },
        {
          key: "avgDiameter",
          label: "Avg D (cm)"
        },
        {
          key: "avgHeight",
          label: "Avg H (m)"
        },
        {
          key: "tonCCO2",
          label: "Ton CCO2 Acum. (2)"
        },
        {
          key: "treeQty",
          label: "Tree Qty"
        }
      ],
      timeFormulaChartData: null,
      timeFormulaChartOptions: null,
      scanBtnStyle: {},
      pcFillStyle: {},
      additionalTableHeight: "240px",
      baseTableHeight: "240px"
    };
  },

  created() {

    this.loadArea();

    Config.getConfig()
      .then((res) => {
        this.config = res.data?.data || res.data;
      });

    if (this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role?.name === 'Administrator') {
      User.getUsers()
        .then((res) => {
          this.users = res.data?.data || res.data;
        })
        .catch(() => {});
    }

    TreeType.getTreeTypes()
      .then((res) => {
        this.allTreeTypes = res.data?.data || res.data;
      });
  },

  mounted() {
    window.addEventListener("resize", this.updateScanBtnPosition);
    window.addEventListener("resize", this.updatePcFillHeight);
    window.addEventListener("resize", this.updateTableHeights);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateScanBtnPosition);
    window.removeEventListener("resize", this.updatePcFillHeight);
    window.removeEventListener("resize", this.updateTableHeights);
    if (this._chartResizeObserver) {
      this._chartResizeObserver.disconnect();
    }
  },

  methods: {
    ...mapActions("NFTcontract", ["createNftToken", "deleteNftToken"]),
    ...mapActions("tokenContract", ["updateBalance"]),
    // ...mapActions("tokenContract", ["createNftToken", "deleteNftToken"]),
    showCheckpointModal() {
      this.$refs.checkpointModal.showModal();
    },
    fmt(n) {
      return new Intl.NumberFormat("de-DE").format(n || 0);
    },
    purposeTitle(kind) {
      const p = this.area && this.area.tree_purpose;
      const n = p ? p[kind] : 0;
      return kind === "trees"
        ? `${this.fmt(n)} tree${n === 1 ? "" : "s"} sold whole (ERC-721)`
        : `${this.fmt(n)} tree${n === 1 ? "" : "s"} for splits (ERC-1155)`;
    },
    async openTreeStock() {
      this.showTreeStockModal = true;
      this.treeStock = null;
      this.treeStockError = "";
      this.treeMove = { to: "trees", count: 1 };
      await this.loadTreeStock();
    },
    async loadTreeStock() {
      this.treeStockLoading = true;
      try {
        const res = await Area.getTreeStock(this.area.id);
        this.treeStock = res.data?.data || res.data;
        this.syncTreePurpose();
        // start with the direction that has trees to move
        if (this.treeStock && !this.treeMovable) this.treeMove.to = this.treeMove.to === "trees" ? "splits" : "trees";
      } catch (err) {
        this.treeStock = null;
        this.treeStockError = err?.response?.data?.error?.message || "Could not load the stock.";
      } finally {
        this.treeStockLoading = false;
      }
    },
    // Keeps the tags and counts of the page in step with the stock just read or changed
    syncTreePurpose() {
      if (!this.treeStock || !this.area) return;
      this.$set(this.area, "tree_purpose", {
        trees: this.treeStock.trees.total,
        splits: this.treeStock.splits.total,
        label: this.treeStock.label
      });
    },
    async confirmTreeMove() {
      if (!this.treeMoveValid || this.treeMoving) return;
      const n = this.treeMove.count;
      const toLabel = this.treeMove.to === "trees" ? "Trees" : "Splits";
      const fromLabel = this.treeMove.to === "trees" ? "Splits" : "Trees";
      const res = await Swal.fire({
        title: `Move ${this.fmt(n)} tree${n === 1 ? "" : "s"} to ${toLabel}?`,
        html: `${this.fmt(n)} free tree${n === 1 ? "" : "s"} of <b>${this.area.name}</b> will stop being stock of ${fromLabel} and become stock of ${toLabel}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Transfer"
      });
      if (!res.isConfirmed) return;

      this.treeMoving = true;
      this.treeStockError = "";
      try {
        const out = await Area.moveTrees(this.area.id, this.treeMove.to, n);
        const data = out.data?.data || out.data;
        this.treeStock = data.stock;
        this.syncTreePurpose();
        this.treeMove.count = 1;
        if (!this.treeMovable) this.treeMove.to = this.treeMove.to === "trees" ? "splits" : "trees";
        Swal.fire("Done!", `${this.fmt(n)} tree${n === 1 ? "" : "s"} moved to ${toLabel}.`, "success");
      } catch (err) {
        this.treeStockError = err?.response?.data?.error?.message || "Could not transfer the trees.";
      } finally {
        this.treeMoving = false;
      }
    },
    async loadArea() {
      const areaId = this.$route.params.id;
      const areaRes = await Area.getAreaInfo(areaId);
      const areaData = areaRes.data?.data || areaRes.data;

      const initialCheckpoint = (areaData.last_checkpoint && areaData.last_checkpoint.data !== undefined)
        ? areaData.last_checkpoint
        : (areaData.last_checkpoint ? { data: areaData.last_checkpoint } : {});

      this.area = { ...areaData, last_checkpoint: initialCheckpoint };
      this.schedulePcFillUpdate();
      // The tour targets real content (map, chart, tabs) that only exists
      // once `area` has loaded — the mixin's generic mounted()-based
      // auto-run fires too early here (still on the plant-growing loading
      // state) and finds nothing to highlight, so it's retriggered here
      // once there's actually something to show.
      this.$nextTick(() => setTimeout(() => maybeAutoRunTour('area-item', tourSteps), 600));

      try {
        if (!this.area.last_checkpoint?.data && this.area.last_checkpoint_id) {
          Checkpoint.getCheckpoints(this.area.last_checkpoint_id)
            .then((res) => {
              const cp = res.data?.data || res.data;
              this.area.last_checkpoint = (cp && cp.data !== undefined) ? cp : { data: cp };
            });
        }

        Area.getBalance(this.area.id).then((res) => {
          this.balance = res.data?.data || res.data;
          this.updateBalance();
          this.schedulePcFillUpdate();
        });

        if (this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role?.name === 'Administrator') {
          Area.getAreasPrivateKey(areaId).then((res) => {
            const pkData = res.data?.data || res.data;
            this.area.privateKey = pkData?.private_key || pkData?.privateKey;
            if (this.area.privateKey) {
              this.$store.dispatch("NFTcontract/fetchNftContract", this.area.privateKey);
            }
          }).catch(() => {});
        }
      } catch (e) {
        console.log(e, "e");
      }

      if (this.area?.last_checkpoint_id) {
        this.renderStatsTable();
      }
      this.schedulePcFillUpdate();
    },
    createNftTokenFromCurrentYear() {

      console.log({ currentDate: moment().format("X") });

      let currentDtIdx = null;
      for (const i in this.theoreticalFormulaStats) {
        console.log(`Compare ${this.theoreticalFormulaStats[i].year} to ${moment().format("yyyy")}`);
        if (this.theoreticalFormulaStats[i].year == parseInt(moment().format("YYYY")))
          currentDtIdx = parseInt(i);
      }
      console.log({ currentDtIdx });


      console.log(this.theoreticalFormulaStats, "this.theoreticalFormulaStats");


      console.log({
        walletAddress: this.area.wallet_address,
        scanDt: moment().utc().startOf("year").format("X"),
        tonCO2Year1: this.theoreticalFormulaStats[currentDtIdx].theoreticalIncrement,
        tonCO2Year2: this.theoreticalFormulaStats[currentDtIdx + 1].theoreticalIncrement,
        tonCO2Year3: this.theoreticalFormulaStats[currentDtIdx + 2].theoreticalIncrement,
        tonCO2Year4: this.theoreticalFormulaStats[currentDtIdx + 3].theoreticalIncrement,
        tonCO2Year5: this.theoreticalFormulaStats[currentDtIdx + 4].theoreticalIncrement,
        tonCO2Year6: this.theoreticalFormulaStats[currentDtIdx + 5].theoreticalIncrement,
        tonCO2Year7: this.theoreticalFormulaStats[currentDtIdx + 6].theoreticalIncrement,
        tonCO2Year8: this.theoreticalFormulaStats[currentDtIdx + 7].theoreticalIncrement,
        tonCO2Year9: this.theoreticalFormulaStats[currentDtIdx + 8].theoreticalIncrement,
        tonCO2Year10: this.theoreticalFormulaStats[currentDtIdx + 9].theoreticalIncrement
      });

      if (currentDtIdx !== "asdy7sa6d78sa678dsad") return;

      if (this.theoreticalFormulaStats.length >= 10 && currentDtIdx != null) {
        Swal.fire({
          title: "Are you sure?",
          html: `You will mint an NFT from current year. You can lose some tokens if not made correctly.`,
          icon: "warning",
          showCancelButton: true
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              title: "Processing",
              html: "Waiting for confirmations",
              didOpen: () => {
                Swal.showLoading();
              },
              allowOutsideClick: () => !Swal.isLoading()
            });
            this.createNftToken(
              {
                walletAddress: this.area.wallet_address,
                scanDt: moment().utc().startOf("year").format("X"),
                tonCO2Year1: this.theoreticalFormulaStats[currentDtIdx].theoreticalIncrement,
                tonCO2Year2: this.theoreticalFormulaStats[currentDtIdx + 1].theoreticalIncrement,
                tonCO2Year3: this.theoreticalFormulaStats[currentDtIdx + 2].theoreticalIncrement,
                tonCO2Year4: this.theoreticalFormulaStats[currentDtIdx + 3].theoreticalIncrement,
                tonCO2Year5: this.theoreticalFormulaStats[currentDtIdx + 4].theoreticalIncrement,
                tonCO2Year6: this.theoreticalFormulaStats[currentDtIdx + 5].theoreticalIncrement,
                tonCO2Year7: this.theoreticalFormulaStats[currentDtIdx + 6].theoreticalIncrement,
                tonCO2Year8: this.theoreticalFormulaStats[currentDtIdx + 7].theoreticalIncrement,
                tonCO2Year9: this.theoreticalFormulaStats[currentDtIdx + 8].theoreticalIncrement,
                tonCO2Year10: this.theoreticalFormulaStats[currentDtIdx + 9].theoreticalIncrement
              }
            )
              .then((res) => {
                const data = {
                  id: this.area.last_checkpoint_id,
                  nft_smart_contract: res.contractAddress,
                  token_id: res.tokenId,
                  token_id_url: res.tokenIdUrl,
                  tx: res.tx,
                  tx_url: res.txUrl,
                  chain: res.chain
                };
                Checkpoint.putCheckpoints(data)
                  .then(() => {
                    this.loadArea();
                    // this.area.last_checkpoint = {
                    //   ...this.area.last_checkpoint,
                    //   ...data
                    // }
                    this.renderStatsTable();

                    Swal.fire(
                      "NFT successfully created",
                      `You've successfully created the NFT with the <a href="${res.tokenIdUrl}" target="_blank">token ID ${res.tokenId}</a>`,
                      "success");
                  });
              })
              .catch((e) => {
                Swal.fire({
                  title: "Error",
                  html: e.message,
                  icon: "error",
                  didOpen: () => {
                    Swal.hideLoading();
                  }
                });
              });
          }
        });

      } else {
        Swal.fire(
          "Error",
          "There was an error with Theoretical info",
          "error"
        );
      }

    },
    createNewToken() {
      console.log({
        walletAddress: this.area.wallet_address,
        scanDt: moment(this.area.datetime).format("X"),
        tonCO2Year1: this.theoreticalFormulaStats[0].theoreticalIncrement,
        tonCO2Year2: this.theoreticalFormulaStats[1].theoreticalIncrement,
        tonCO2Year3: this.theoreticalFormulaStats[2].theoreticalIncrement,
        tonCO2Year4: this.theoreticalFormulaStats[3].theoreticalIncrement,
        tonCO2Year5: this.theoreticalFormulaStats[4].theoreticalIncrement,
        tonCO2Year6: this.theoreticalFormulaStats[5].theoreticalIncrement,
        tonCO2Year7: this.theoreticalFormulaStats[6].theoreticalIncrement,
        tonCO2Year8: this.theoreticalFormulaStats[7].theoreticalIncrement,
        tonCO2Year9: this.theoreticalFormulaStats[8].theoreticalIncrement,
        tonCO2Year10: this.theoreticalFormulaStats[9].theoreticalIncrement
      });

      // if (this.area.wallet_address !== "asdy7sa6d78sa678dsad") return;
      if (this.theoreticalFormulaStats.length >= 10) {
        Swal.fire({
          title: "Are you sure?",
          html: `Once you have created the NFT, you won't be able to import or modify the information from this forest.`,
          icon: "warning",
          showCancelButton: true
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              title: "Processing",
              html: "Waiting for confirmations",
              didOpen: () => {
                Swal.showLoading();
              },
              allowOutsideClick: () => !Swal.isLoading()
            });
            this.createNftToken(
              {
                walletAddress: this.area.wallet_address,
                scanDt: moment(this.area.datetime).format("X"),
                tonCO2Year1: this.theoreticalFormulaStats[0].theoreticalIncrement,
                tonCO2Year2: this.theoreticalFormulaStats[1].theoreticalIncrement,
                tonCO2Year3: this.theoreticalFormulaStats[2].theoreticalIncrement,
                tonCO2Year4: this.theoreticalFormulaStats[3].theoreticalIncrement,
                tonCO2Year5: this.theoreticalFormulaStats[4].theoreticalIncrement,
                tonCO2Year6: this.theoreticalFormulaStats[5].theoreticalIncrement,
                tonCO2Year7: this.theoreticalFormulaStats[6].theoreticalIncrement,
                tonCO2Year8: this.theoreticalFormulaStats[7].theoreticalIncrement,
                tonCO2Year9: this.theoreticalFormulaStats[8].theoreticalIncrement,
                tonCO2Year10: this.theoreticalFormulaStats[9].theoreticalIncrement
              }
            )
              .then((res) => {
                const data = {
                  id: this.area.last_checkpoint_id,
                  nft_smart_contract: res.contractAddress,
                  token_id: res.tokenId,
                  token_id_url: res.tokenIdUrl,
                  tx: res.tx,
                  tx_url: res.txUrl,
                  chain: res.chain
                };
                Checkpoint.putCheckpoints(data)
                  .then(() => {
                    this.loadArea();
                    // this.area.last_checkpoint = {
                    //   ...this.area.last_checkpoint,
                    //   ...data
                    // }
                    this.renderStatsTable();

                    Swal.fire(
                      "NFT successfully created",
                      `You've successfully created the NFT with the <a href="${res.tokenIdUrl}" target="_blank">token ID ${res.tokenId}</a>`,
                      "success");
                  });
              })
              .catch((e) => {
                Swal.fire({
                  title: "Error",
                  html: e.message,
                  icon: "error",
                  didOpen: () => {
                    Swal.hideLoading();
                  }
                });
              });
          }
        });

      } else {
        Swal.fire(
          "Error",
          "There was an error with Theoretical info",
          "error"
        );
      }

    },
    deleteToken() {
      Swal.fire({
        title: "Are you sure?",
        html: `You will burn this NFT, this means that you won't be the owner of this NFT anymore but you will be able to import or modify the information from this forest.`,
        icon: "warning",
        showCancelButton: true
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Processing",
            html: "Waiting for confirmations",
            didOpen: () => {
              Swal.showLoading();
            },
            allowOutsideClick: () => !Swal.isLoading()
          });
          this.deleteNftToken(this.area.token_id)
            .then((res) => {
              const data = {
                id: this.area.last_checkpoint_id,
                deleted_token: 1
              };
              Checkpoint.putCheckpoints(data)
                .then(() => {
                  // Todo: why i use this? need to correct
                  this.area.last_checkpoint = {
                    ...this.area.last_checkpoint,
                    ...data
                  };
                  Swal.fire(
                    "NFT successfully deleted",
                    `You've successfully deleted the NFT with the <a href="${res.tokenIdUrl}" target="_blank">token ID ${res.tokenId}</a>`,
                    "success");
                });
            })
            .catch((e) => {
              Swal.fire({
                title: "Error",
                html: e.message,
                icon: "error",
                didOpen: () => {
                  Swal.hideLoading();
                }
              });
            });
        }
      });

    },
    async renderStatsTable() {
      let areaLastCheckpointTotalCO2 = this.area.CO2Comparison;
      this.practicalStats = [];

      const res = await Area.getCheckpoints(this.area.id);
      const checkpoints = res.data?.data || res.data || [];

      console.log(checkpoints);

      console.log(this.area, "this.area");

      for (const i in checkpoints) {
        const checkpoint = checkpoints[i];

        let avgDiameter = [];
        let avgHeight = [];
        let tonCCO2 = [];
        let treeQty = [];

        console.log({ checkpoint });
        console.log({ "checkpoint.treeTypes": checkpoint.treeTypes });

        for (const treeType of (checkpoint.treeTypes || [])) {
          avgDiameter.push(`${treeType.name}: ${(treeType.diameterAverage || 0).toFixed(2)}`);
          avgHeight.push(`${treeType.name}: ${(treeType.heightAverage || 0).toFixed(2)}`);
          tonCCO2.push(`<b>${treeType.name}</b>: ${(treeType.CO2 || 0).toFixed(2)}`);
          treeQty.push(`${treeType.name}: ${treeType.qty || 0}`);
        }


        this.practicalStats.push({
          year: parseInt(moment.utc(checkpoint.datetime).format("YYYY")),
          date: moment(checkpoint.datetime).format("DD/MM/YYYY"),
          tokenIdUrl: checkpoint.token_id_url,
          tokenId: checkpoint.token_id,
          name: this.area.name,
          contractAddress: checkpoint.nft_smart_contract,
          chain: checkpoint.chain,
          tx: checkpoint.tx,
          txUrl: checkpoint.tx_url,
          deletedToken: checkpoint.deleted_token,
          avgDiameter,
          avgHeight,
          tonCCO2,
          treeTypes: checkpoint.treeTypes,
          totalTonCCO2: (parseFloat(checkpoint.CO2) || 0).toFixed(5),
          treeQty,
          totalTreeQty: checkpoint.qty
        });
      }


      this.practicalStats.reverse();

      this.theoreticalFormulaStats = [];
      let datasets = [];
      let annotations = [];
      let labels = [];
      console.log({ areaa1: this.area });
      console.log({ areaa2: { ...this.area } });
      if (this.area.last_checkpoint_id) {
        let data = [];
        let emissionsCumulativeByIndex = [];

        // let dataAdditional = [];
        let dataAdditionalOptional = {};
        let totalDataAdditionalOptional = {};
        let totalBaselineAdditional = {};
        let baselineAdditional = {};
        let theoreticalIncrementAcumProfit = 0;
        let diff = 0;
        let diffBase = 0;
        let additionalBase = 0;
        let lastAdditionality = 0;
        let lastBase = 0;

        // ISO 14064-2: emissions (e.g. fuel burned by forestry machinery)
        // permanently subtract from additionality from the year they
        // occurred onward — grouped by year here, then accumulated as the
        // main loop below walks every year in chronological order (past
        // included), so a past emission still reduces every future
        // cumulative total even though past years themselves aren't
        // rendered in the "Additional" table (which only shows the
        // current year onward).
        const emissionsByYear = {};
        for (const emission of this.area.emissions || []) {
          const emissionYear = new Date(emission.date).getFullYear();
          emissionsByYear[emissionYear] = (emissionsByYear[emissionYear] || 0) + parseFloat(emission.ton_co2 || 0);
        }
        let cumulativeEmissions = 0;

        for (const j in this.area.theoreticalStats) {

          const theoreticalStat = this.area.theoreticalStats[j];

          // theoreticalStat.year only gets its final calendar-year value
          // reassigned further down (this.area.year < 10000 branch) — work
          // out that same effective year here so the emissions lookup is
          // correct in both branches without reordering the rest of this
          // method.
          const effectiveYear = this.area.year < 10000 ? (this.area.year + theoreticalStat.index) : theoreticalStat.year;
          cumulativeEmissions += emissionsByYear[effectiveYear] || 0;

          lastAdditionality = diff;
          lastBase = diffBase;

          if (!totalDataAdditionalOptional) {
            totalDataAdditionalOptional = [];
          }
          totalDataAdditionalOptional[j] = 0;

          if (!totalBaselineAdditional) {
            totalBaselineAdditional = [];
          }
          totalBaselineAdditional[j] = 0;

          if (theoreticalStat.year >= parseInt(moment().format("YYYY"))) {


            // this.theoreticalFormulaStats.push({
            //   ...theoreticalStat,
            //   theoreticalIncrementAcumProfit
            // });


            if (this.blockchainTheoreticalStats.length < 10) {
              this.blockchainTheoreticalStats.push({
                year: theoreticalStat.year,
                "Yearly Increment": new Intl.NumberFormat("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(theoreticalStat.theoreticalIncrement) + " Ton CCO2"
              });
            }
          }


          for (const additionality of this.area.additionalities) {

            if (!baselineAdditional[additionality.type]) {
              baselineAdditional[additionality.type] = [];
            }

            if (!dataAdditionalOptional[additionality.type]) {
              dataAdditionalOptional[additionality.type] = [];
            }


            baselineAdditional[additionality.type][j] = baselineAdditional[additionality.type][j] || 0;

            // 0 (not null) for years this additionality type has no matching
            // value entry — with the stacked area chart below, a null here
            // breaks the fill for that one index and shows up as a small
            // gap/hole in the stacked area at that point.
            dataAdditionalOptional[additionality.type][j] = dataAdditionalOptional[additionality.type][j] || 0;
            // let found = false;
            for (const i in additionality.values) {
              const value = additionality.values[i];

              // console.log(additionality.type+' theoreticalStat.year '+theoreticalStat.year+' == (value.year '+value.year+' + additionality.starting_year '+additionality.starting_year+') '+(value.year + additionality.starting_year))
              if (theoreticalStat.year == (value.year + additionality.starting_year)) {
                dataAdditionalOptional[additionality.type][j] = (dataAdditionalOptional[additionality.type][j] || 0) + value.value;
                baselineAdditional[additionality.type][j] = (baselineAdditional[additionality.type][j] || 0) + value.baseline_value;
                totalDataAdditionalOptional[j] += parseFloat(dataAdditionalOptional[additionality.type][j]) || 0;
                totalBaselineAdditional[j] += parseFloat(baselineAdditional[additionality.type][j]) || 0;
                // found = true;
              }


            }

            // console.log(additionality.type+"parseFloat(dataAdditionalOptional[additionality.type][j])", parseFloat(dataAdditionalOptional[additionality.type][j]));
            //  totalDataAdditionalOptional[j] += parseFloat(dataAdditionalOptional[additionality.type][j]) || 0;


            // if (!found) {
            //   dataAdditionalOptional[additionality.type][j] = null;
            // }


          }

          if (this.area.year < 10000) {
            theoreticalStat.year = this.area.year + theoreticalStat.index;

            if (!dataAdditionalOptional[""]) {
              dataAdditionalOptional[""] = [];
            }
            dataAdditionalOptional[""][j] = 0;

            // console.log("111-totalDataAdditionalOptional[j]", totalDataAdditionalOptional[j]);
// let found = false;
            for (const practicalStat of this.practicalStats.reverse()) {
              if (practicalStat.year == theoreticalStat.year) {
                additionalBase = parseFloat(practicalStat.totalTonCCO2) - theoreticalStat.totalTonCCO2;

                if (additionalBase < 0)
                  additionalBase = 0;

                console.log({ additionalBase });

                // found = true;
              }
            }
            console.log("111111-totalBaselineAdditional[j]", totalBaselineAdditional[j]);


            dataAdditionalOptional[""][j] = additionalBase;
            diff = totalDataAdditionalOptional[j] + additionalBase - cumulativeEmissions;
            diffBase = theoreticalStat.totalTonCCO2 + totalBaselineAdditional[j] < 0 ? 0 : theoreticalStat.totalTonCCO2 + totalBaselineAdditional[j];


            labels.push(this.area.year + theoreticalStat.index);

            // Past years are included too (not just current-year-onward) so
            // the table can default-scroll to "now" while still letting the
            // user scroll up into history.
            this.theoreticalFormulaStats.push({
              ...theoreticalStat,
              totalTonCCO2: diffBase,
             theoreticalIncrement: diffBase - lastBase > 0 ? diffBase - lastBase : 0,
              totalAdditionalTonCCO2: diff,
              theoreticalAdditionalIncrement: diff - lastAdditionality > 0 ? diff - lastAdditionality : 0,
              emissionsTonCo2: emissionsByYear[theoreticalStat.year] || 0,
              theoreticalIncrementAcumProfit
            });

            if (theoreticalStat.year >= parseInt(moment().format("YYYY")) && this.blockchainTheoreticalStats.length < 10) {
              this.blockchainTheoreticalStats.push({
                year: theoreticalStat.year,
                "Yearly Increment": new Intl.NumberFormat("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(theoreticalStat.theoreticalIncrement) + " Ton CCO2"
              });
            }
          } else {



                        console.log("222222222-totalBaselineAdditional[j]", totalBaselineAdditional[j]);


                         diffBase = theoreticalStat.totalTonCCO2 + totalBaselineAdditional[j] < 0 ? 0 : theoreticalStat.totalTonCCO2 + totalBaselineAdditional[j];


              labels.push(theoreticalStat.index + " Years");

              // Past years included too — see the matching comment in the
              // `if` branch above.
              this.theoreticalFormulaStats.push({
                ...theoreticalStat,
                totalTonCCO2: diffBase,
             theoreticalIncrement: diffBase - lastBase > 0 ? diffBase - lastBase : 0,
                theoreticalIncrementAcumProfit
              });

              if (theoreticalStat.year >= parseInt(moment().format("YYYY")) && this.blockchainTheoreticalStats.length < 10) {
                this.blockchainTheoreticalStats.push({
                  year: theoreticalStat.year,
                  "Yearly Increment": new Intl.NumberFormat("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(theoreticalStat.theoreticalIncrement) + " Ton CCO2"
                });
              }



          }
          // this.theoreticalFormulaStats[j].totalAdditionalTonCCO2 = diff;

          data.push(theoreticalStat.totalTonCCO2.toFixed(2));
          emissionsCumulativeByIndex.push(cumulativeEmissions);


          // dataAdditional.push(tonCCO2AdditionalRequiredPerTree.toFixed(5));

        }

        for (const i in baselineAdditional) {

          const additionality = baselineAdditional[i];

          for (const j in additionality) {

            if (additionality[j]) {
              data[j] = Math.abs(parseFloat(data[j]) + additionality[j]);
            }

          }

        }

        const currentStats = this.theoreticalFormulaStats.filter(({ year }) => year === parseInt(moment().format("YYYY")))[0];


        this.currentYearTheoreticalIncrement = currentStats ? parseFloat(currentStats?.theoreticalIncrement) : null;
        this.currentYearTheoreticalAdditionalIncrement = currentStats ? parseFloat(currentStats?.theoreticalAdditionalIncrement) : null;

        datasets.push({
          label: "Ton CCO2",
          borderColor: "#4b6b41",
          backgroundColor: "#4b6b41",
          borderWidth: 0,
          hoverradius: 0,
          radius: 0,
          fill: true,
          data
        });

        // ISO 14064-2: emissions shrink the stacked "Additional" area(s)
        // directly (distributed proportionally across whichever
        // additionality types are present at that point) instead of adding
        // a separate negative dataset. This chart's y-axis is `stacked:
        // true` — a dedicated negative series, even a tiny one, pushes
        // Chart.js's autoscale to extend the axis well below zero (e.g. to
        // -100) for the *whole* chart, which visually reads as if the
        // "Ton CCO2" baseline itself went negative even though it never
        // does. Reducing the additional values in place never goes
        // negative (clamped at 0) and never touches "Ton CCO2" at all.
        const additionalTypeKeys = Object.keys(dataAdditionalOptional);
        for (let idx = 0; idx < labels.length; idx++) {
          const emissionAtIdx = emissionsCumulativeByIndex[idx] || 0;
          if (!emissionAtIdx) continue;

          const totalAtIdx = additionalTypeKeys.reduce((sum, key) => sum + (parseFloat(dataAdditionalOptional[key][idx]) || 0), 0);
          if (totalAtIdx <= 0) continue;

          for (const key of additionalTypeKeys) {
            const value = parseFloat(dataAdditionalOptional[key][idx]) || 0;
            const share = value / totalAtIdx;
            dataAdditionalOptional[key][idx] = Math.max(0, value - emissionAtIdx * share);
          }
        }

        console.log({ dataAdditionalOptional });

        for (const i in dataAdditionalOptional) {
          const data = dataAdditionalOptional[i];

          datasets.push({
            label: `Additional ${i}`,
            borderColor: "#628d3d",
            backgroundColor: "#628d3d",
            borderWidth: 0,
            hoverradius: 0,
            radius: 0,
            fill: true,
            data
          });
        }

        // ISO 14064-2: a real stacked segment, same default stack group as
        // the "Additional X" areas above it (no distinct `stack`/`yAxisID`
        // override) — Chart.js draws datasets in push order, so being
        // pushed last stacks this directly on top of the reduced Additional
        // areas, in yellow. E.g. 100 additionality with 20 lost to
        // emissions renders as 80 (green) + 20 (yellow) stacked to the
        // original 100 — the yellow band IS the amount subtracted. The
        // plotted value is positive (stacking needs a positive height);
        // the tooltip label callback below negates it for display, since
        // it's still a subtraction from the seller's perspective.
        if (this.area.emissions && this.area.emissions.length) {
          datasets.push({
            label: "Emissions",
            borderColor: "#e8b339",
            backgroundColor: "#e8b339",
            borderWidth: 0,
            pointRadius: 0,
            fill: true,
            // Plain 0 (not null) for years before the first emission — this
            // dataset shares the main 0-based stack with Additional/Base, so
            // a 0 just contributes nothing and sits flush at the bottom of
            // the stack. `null` left a visible gap between the last "empty"
            // point and the first real one, since Chart.js doesn't draw a
            // line/fill edge connecting into a null point.
            data: emissionsCumulativeByIndex
          });
        }

        if (this.area.year < 10000) {
          annotations.push({
            // afterDatasetsDraw (not afterDraw) so the line is drawn before
            // the tooltip — afterDraw fires last and was painting the line
            // on top of an open tooltip, hiding its values.
            drawTime: "afterDatasetsDraw",
            id: "scan-line",
            type: "line",
            mode: "vertical",
            scaleID: "x-axis-0",
            value: parseInt(moment().format("YYYY")),
            label: {
              enabled: true,
              content: "Now",
              position: "top"
            },
            borderColor: "#ffdd00",
            borderWidth: 2
          });

          // Todo: show a line for each checkpoint
          // for (const i in checkpoints) {
          //   annotations.push({
          //     drawTime: "afterDraw",
          //     id: "a-line-cp-" + (parseInt(i) + 1),
          //     type: "line",
          //     mode: "horizontal",
          //     scaleID: "y-axis-0",
          //     value: checkpoints[i].CO2,
          //     label: {
          //       enabled: true,
          //       content: "Scan " + (parseInt(i) + 1),
          //       position: "left"
          //     },
          //     borderColor: "#ffdd00",
          //     borderWidth: 2
          //   });
          // }

        } else {
          annotations.push({
            drawTime: "afterDatasetsDraw", // before the tooltip, not after it
            id: "scan-line",
            type: "line",
            mode: "horizontal",
            scaleID: "y-axis-0",
            value: areaLastCheckpointTotalCO2,
            borderColor: "#ffdd00",
            borderWidth: 2
          });
        }


//         annotations.push({
//           type: 'box',
//           drawTime: 'beforeDatasetsDraw',
//           id: 'a-box-1',
//           xScaleID: 'x-axis-0',
//           yScaleID: 'y-axis-0',
//           xMin: 25,
//           xMax: 60,
//           yMin: 0,
//           borderColor: '#16311c9e',
//           backgroundColor: '#16311c9e',
//         })
      }
      this.renderTimeFormulaChart(labels, datasets, annotations);

    },
    renderTimeFormulaChart(labels, datasets, annotations) {

      this.timeFormulaChartData = {
        labels,
        datasets
      };

      this.timeFormulaChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {

              var label = data.datasets[tooltipItem.datasetIndex].label || "";

              if (label) {
                label += ": ";
              }
              // Plotted as a positive stacked segment (Chart.js needs a
              // positive height to stack it on top of Additional), but
              // it's a subtraction from the seller's perspective — shown
              // negative here to match that.
              var displayValue = label.startsWith("Emissions") ? -tooltipItem.yLabel : tooltipItem.yLabel;
              label += new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(displayValue);
              return label;
            },

            footer: (tooltipItems, data) => {
              if (tooltipItems === 1) {
                console.log({ tooltipItems: "1" || tooltipItems });
                console.log({ data: "1" || data });
              }

              let sumBaseLine = 0;
              let sumAdditionality = 0;
              for (const i in tooltipItems) {
                const tooltipItem = tooltipItems[i];
                const datasetLabel = data.datasets[i].label;
                // "Emissions" is stacked as its own yellow segment (see
                // renderStatsTable) — it's neither Base nor Additional
                // credited CO2, so it must be excluded here or it silently
                // gets counted as Base Line (anything not "Additional ..."
                // used to mean "Ton CCO2" back when those were the only two
                // options).
                if (datasetLabel === "Emissions") continue;
                if (datasetLabel.includes("Additional"))
                  sumAdditionality += tooltipItem.yLabel;
                else {
                  sumBaseLine += tooltipItem.yLabel;
                }
              }
              return `T. Additional: ${Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(sumAdditionality)}\nT. Base Line: ${Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(sumBaseLine)}
`;
//               return ``;
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "y-axis-0",
              stacked: true,
              ticks: {
                callback: function(label) {
                  return new Intl.NumberFormat("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(label);
                }
              }
            }
          ]
        },
        hover: {
          mode: "index",
          intersect: false
        },
        annotation: {
          drawTime: "afterDatasetsDraw",
          annotations
        }
      };

      this.scheduleScanBtnPosition();
      this.$nextTick(() => this.setupChartResizeObserver());
    },
    // vue-chartjs re-renders the chart asynchronously when chartData/options
    // change, so the annotation plugin hasn't drawn (and computed pixel
    // coordinates for) the "scan-line" yet on the same tick — poll a few
    // times a beat apart instead of relying on a single nextTick.
    scheduleScanBtnPosition() {
      let attempts = 0;
      const tryUpdate = () => {
        attempts++;
        const found = this.updateScanBtnPosition();
        if (!found && attempts < 10) {
          setTimeout(tryUpdate, 80);
        }
      };
      this.$nextTick(tryUpdate);
    },
    updateScanBtnPosition() {
      const chartComponent = this.$refs.timeChart;
      const chart = chartComponent && chartComponent.$data && chartComponent.$data._chart;
      const el = chart && chart.annotation && chart.annotation.elements && chart.annotation.elements["scan-line"];
      const view = el && el._view;
      if (!view || view.x1 == null) {
        return false;
      }

      if (view.mode === "horizontal") {
        // Sit just above the line, hugging the chart's left edge.
        this.scanBtnStyle = {
          top: Math.max(4, view.y1 - 30) + "px",
          left: (view.x1 + 6) + "px"
        };
      } else {
        // Vertical "Now" line: sit at its top, centered on it.
        this.scanBtnStyle = {
          top: (view.y1 + 4) + "px",
          left: Math.max(4, view.x1 - 60) + "px"
        };
      }
      return true;
    },
    // The header can wrap to a second line depending on content (owner
    // name length, whether Cadastre is present, viewport width), so a
    // fixed calc(100vh - Npx) can't account for its real height — measure
    // the row's actual top offset instead, after layout has settled.
    schedulePcFillUpdate() {
      // A couple retries a beat apart — on first load the viewport/layout
      // isn't always settled yet at the first check (e.g. right after a
      // resize), so one measurement can under/overshoot.
      [60, 250, 600].forEach((delay) => {
        setTimeout(() => {
          // Attach first — if the observer misses this window's redraws
          // for any reason, at least it's watching for the next one.
          this.setupChartResizeObserver();
          this.updatePcFillHeight();
          this.updateTableHeights();
          // The growth-chart-wrap's own size just changed, which moves the
          // "scan-line" annotation's pixel position — resync the button.
          this.scheduleScanBtnPosition();
        }, delay);
      });
    },
    // Chart.js resizes its canvas whenever growth-chart-wrap's own box
    // changes (responsive:true, via an internal detector) — completely
    // independent of window "resize" events and of our own height-fill
    // retries above. Watch the element directly so the scan button stays
    // pinned to the line through any of those redraws, however they're
    // triggered.
    setupChartResizeObserver() {
      if (this._chartResizeObserver || typeof ResizeObserver === "undefined") return;
      const el = this.$refs.growthChartWrapEl;
      if (!el) return;
      this._chartResizeObserver = new ResizeObserver(() => this.scheduleScanBtnPosition());
      this._chartResizeObserver.observe(el);
    },
    // Fires on tab/sub-tab change: only the table that just became active
    // can be measured (hidden panes report a zero-size rect), so re-measure
    // after the switch settles.
    scheduleTableHeightUpdate() {
      this.$nextTick(() => setTimeout(this.updateTableHeights, 60));
    },
    updateTableHeights() {
      this.measureTableHeight("additionalTableEl", "additionalTableHeight");
      this.measureTableHeight("baseTableEl", "baseTableHeight");
      // The sticky-header height above may have just changed — wait a tick
      // so the scroll container's own max-height/overflow reflects it
      // before we try to scroll inside it.
      this.$nextTick(() => {
        this.scrollTableToCurrentYear("additionalTableEl");
        this.scrollTableToCurrentYear("baseTableEl");
      });
    },
    currentYearRowClass(item) {
      if (!item) return "";
      return item.year === parseInt(moment().format("YYYY")) ? "current-year-row" : "";
    },
    // Both tables include the forest's full history now (not just current
    // year onward) so users can scroll up to see past years — but they
    // should land on "now" by default rather than at the very start.
    scrollTableToCurrentYear(refName) {
      const table = this.$refs[refName];
      const el = table && table.$el;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return; // hidden (inactive) tab pane

      // bootstrap-vue puts the "b-table-sticky-header" scroll wrapper class
      // directly on the table component's own root element, not a
      // descendant of it — querySelector() alone would never match it.
      const scrollContainer = el.classList.contains("b-table-sticky-header")
        ? el
        : el.querySelector(".b-table-sticky-header");
      if (!scrollContainer) return;

      const currentYear = parseInt(moment().format("YYYY"));
      const idx = this.theoreticalFormulaStats.findIndex((s) => s.year === currentYear);
      const rows = el.querySelectorAll("tbody tr");
      if (idx === -1 || !rows[idx]) return;

      // The sticky header sits on top of the scrolled content at the same
      // scrollTop, covering whatever's at the very top of it — landing at
      // exactly the row's offsetTop puts the CURRENT year row underneath
      // the header, not just below it. Subtracting the header's own height
      // instead scrolls one row further, so the previous year ends up
      // hidden behind the header and the current year is the first fully
      // visible row.
      const headerEl = el.querySelector("thead");
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
      scrollContainer.scrollTop = Math.max(0, rows[idx].offsetTop - headerHeight);
    },
    measureTableHeight(refName, dataKey) {
      if (window.innerWidth < 992) {
        this[dataKey] = "240px";
        return;
      }
      const table = this.$refs[refName];
      const el = table && table.$el;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        // Hidden (inactive) tab pane — leave the previous value alone.
        return;
      }
      const footer = document.querySelector(".app-footer");
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
      // Same fixed page chrome as updatePcFillHeight(), plus the chart
      // card's own bottom padding below the table (pb-2, 8px) and a bit of
      // extra safety margin so the sticky-header wrapper never outgrows
      // the card that clips it.
      const available = window.innerHeight - rect.top - footerHeight - 68;
      this[dataKey] = Math.max(160, available) + "px";
    },
    updatePcFillHeight() {
      if (window.innerWidth < 992) {
        this.pcFillStyle = {};
        return;
      }
      const row = this.$refs.overviewRow;
      const el = row && (row.$el || row);
      if (!el || typeof el.getBoundingClientRect !== "function") {
        this.pcFillStyle = {};
        return;
      }
      const top = el.getBoundingClientRect().top;
      // Leave room for the app footer, which sits right after this page in
      // normal flow — otherwise filling to 100vh pushes it just offscreen.
      // FIXED_BOTTOM_CHROME covers spacing that doesn't depend on content
      // height (column bottom margin + <main>'s own bottom padding, see
      // .content-area in App.vue), so it stays correct without having to
      // re-measure the footer's position after every height change.
      const footer = document.querySelector(".app-footer");
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
      const available = window.innerHeight - top - footerHeight - 48;
      this.pcFillStyle = { height: Math.max(360, available) + "px" };
    },
    async changeTreeType(i) {
      let oldTreeType = { ...this.area.treeTypes[i] };

      let inputOptions = this.allTreeTypes.reduce((json, val) => {
        json[val.id] = val.name;
        return json;
      }, {});

      const { value: treeTypeId } = await Swal.fire({
        title: `Change ${oldTreeType.name} tree type`,
        input: "select",
        inputOptions,
        inputPlaceholder: "Select a tree type",
        // confirmButtonColor: '#4b6b41',
        showCancelButton: true,
        inputValidator: (treeTypeId) => {
          return new Promise((resolve) => {
            resolve();
// Todo: check this
            let areaTreeTypesIds = Object.values(this.area.last_checkpoint.data).map(oldTreeType => oldTreeType.id);
            if (areaTreeTypesIds.includes(parseInt(treeTypeId))) {
              resolve(`You can't repeat a tree type in the same area`);
            } else {
              resolve();
            }
          });
        }
      });

      if (treeTypeId) {
        TreeType.getTreeTypes(treeTypeId)
          .then((res) => {
            // const math = create(all);

            let treeType = res.data;


            if (this.area.last_checkpoint_id) {

              let lastCheckpointTreeTypeData = this.area.treeTypes[i];
              lastCheckpointTreeTypeData.id = parseInt(treeTypeId);
              lastCheckpointTreeTypeData.reference = treeType.reference;
              lastCheckpointTreeTypeData.name = treeType.name;
              lastCheckpointTreeTypeData.densityKg = treeType.density_kg;
              lastCheckpointTreeTypeData.practicalFormula = treeType.practical_formulas;
              lastCheckpointTreeTypeData.theoreticalFormula = treeType.time_formula;

              renderTreeTypeData(this.area.treeTypes, lastCheckpointTreeTypeData.datetime).then((lastCheckpoint) => {
                // lastCheckpoint.data.treeTypes = {...lastCheckpoint.data.treeTypes[i]};
                // delete lastCheckpoint.data.treeTypes[i];

                Checkpoint.putCheckpoints(lastCheckpoint).then(() => {
                  Swal.fire(
                    "Done!",
                    `Tree type successfully changed from ${oldTreeType.name} to ${treeType.name}`,
                    "success"
                  );
                  this.loadArea();
                });
              });


            }
          });
      }
    },
    async delAdditionality(i) {


      Swal.fire({
        title: "Are you sure?",
        text: "You will delete an additionality",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes"
      }).then(async (result) => {
        if (result.isConfirmed) {


          console.log({ area3: this.area });
          console.log({ i });


          Swal.fire({
            title: "Processing",
            html: "Wait a moment...",
            didOpen: () => {
              Swal.showLoading();
            },
            allowOutsideClick: () => !Swal.isLoading()
          });

          const additionalityToDelete = this.area.additionalities[i];
          const additionalityId = additionalityToDelete?.id;
          this.area.additionalities.splice(i, 1);
          const remainingAdditionalities = this.area.additionalities.map(r => r.id);

          try {
            if (additionalityId) {
              await Additionalities.delAdditionalities(additionalityId);
            }
            await Area.putAreas({
              id: this.area.id,
              additionalities: remainingAdditionalities
            });
            await this.loadArea();
            Swal.fire(
              "Deleted successfully",
              `Additionality deleted!`,
              "success"
            );
          } catch (e) {
            console.error("Error deleting additionality:", e);
            await this.loadArea();
            Swal.fire(
              "Error",
              e.response?.data?.error?.message || e.message || "Error deleting additionality",
              "error"
            );
          }

        }
      });

    },
    async changeOwner() {

      let inputOptions = this.users.reduce((json, val) => {
        json[val.id] = val.name || "";
        return json;
      }, {});

      const res = await Swal.fire({
        title: `Change ${this.area.user_name || ""} owner`,
        input: "select",
        inputOptions,
        inputPlaceholder: "Select an owner",
        // confirmButtonColor: '#4b6b41',
        showCancelButton: true
      });

      // if (users_permissions_user) {
      if (res.isConfirmed)
        Area.putAreas({
          id: this.area.id,
          users_permissions_user: res.value || null
        }).then(() => {
          Swal.fire(
            "Done!",
            `Owner successfully changed`,
            "success"
          );
          this.loadArea();
        });

      // }
    },
    async addAdditionality() {


      const { value: additionality } = await Swal.fire({
        title: "Select additionality type",
        input: "select",
        inputOptions: {

          fertilizing: "Fertilizing",
          thinning: "Thinning",
          postponedFinalCut: "Postponed final cut"
        },
        inputPlaceholder: "Select additionality",
        showCancelButton: true,
        inputValidator: (value) => {
          return new Promise((resolve) => {
            if (value) {
              resolve();
            } else {
              resolve("You need to select one");
            }
          });
        }
      });


      if (!additionality) return;

      try {
        if (additionality == "fertilizing") {


        let fertilizingsData = (await Fertilizings.getFertilizings()).data;

        console.log({ fertilizings: fertilizingsData });
        let fertilizings = {};

        for (let data of fertilizingsData) {
          fertilizings[data.id] = data.name || data.fertilizing;
        }

        const { value: fertilizingId } = await Swal.fire({
          title: "Select fertilizing type",
          input: "select",
          inputOptions: fertilizings,
          inputPlaceholder: "Select fertilizing",
          showCancelButton: true,
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (value) {
                resolve();
              } else {
                resolve("You need to select one");
              }
            });
          }
        });


        if (!fertilizingId) return;

        const { value: date } = await Swal.fire({
          title: "Select additionality start date",
          input: "date",
          showCancelButton: true,
          didOpen: () => {
            // const today = (new Date()).toISOString();
            // Swal.getInput().min = today.split("T")[0];
          }
        });
        if (!date) return;

        const startingDt = moment(date).format("YYYY-MM-DD");
        const defaultYear = parseInt(moment(date).format("YYYY")) + 1;

        const { value: year } = await Swal.fire({
          title: "Select additionality start date",
          input: "number",
          inputValue: defaultYear,
          showCancelButton: true
        });
        if (!year) return;

        // return;

        // if (fertilizings == "AMERICA") {


        let areaLastCheckpointData = this.area.last_checkpoint.theoretical_stats;
        let areaLastCheckpointTreeTypesData = this.area.last_checkpoint.data.treeTypes;

        let yearIndex = 0;
        for (const i in areaLastCheckpointData) {
          if (areaLastCheckpointData[i].year == year) {
            yearIndex = parseInt(i);
            break;
          }
        }


        for (const treeType of areaLastCheckpointTreeTypesData) {

          let res = await TreeType.getTreeTypes(treeType.id);
          const dbTreeType = res.data;
          let lengthYears = 0;
          let yearValues = {};
          const ttFertilizings = dbTreeType.tree_types_fertilizings || dbTreeType.treeTypesFertilizings || [];
          for (const dbTreeTypeElement of ttFertilizings) {
            console.log({ dbTreeTypeElement });
            if (dbTreeTypeElement.Factors.length > lengthYears) lengthYears = dbTreeTypeElement.Factors.length;
            for (const factor of dbTreeTypeElement.Factors) {
              console.log({ factor });
              const elemFertilizingId = typeof dbTreeTypeElement.fertilizing === 'object' 
                ? dbTreeTypeElement.fertilizing?.id 
                : (dbTreeTypeElement.fertilizing || dbTreeTypeElement.id);
              if (elemFertilizingId == fertilizingId) {
                if (yearValues[factor.year + yearIndex])
                  yearValues[factor.year + yearIndex] += factor.factor || 0;
                else
                  yearValues[factor.year + yearIndex] = factor.factor || 0;

              }
            }

          }


          console.log({ yearValues });
          console.log({ dbTreeType });

          let additionalities = [];
          let accumulatedAdditionality = 0;
          for (const i in treeType.theoreticalStats) {
            if (yearValues[i] != null) {
              console.log(yearValues[i], "yearValues[i]");
              const theoreticalStat = treeType.theoreticalStats[i];
              console.log({ theoreticalStat });
              accumulatedAdditionality += theoreticalStat.theoreticalIncrement * (yearValues[i] || 0);
              let additionality = {
                year: additionalities.length,
                value: accumulatedAdditionality
              };

              additionalities.push(additionality);

            }
          }


          Swal.fire({
            title: "Processing",
            html: "Wait a moment...",
            didOpen: () => {
              Swal.showLoading();
            },
            allowOutsideClick: () => !Swal.isLoading()
          });

          console.log({ year });
          console.log({ fertilizings: fertilizingsData });
          console.log({ fertilizing: fertilizingId });
          console.log({ lengthYears });
          console.log({ dbTreeType });

          await Additionalities.postAdditionalities({
            priority: 1,
            type: `${fertilizings[fertilizingId]} fertilizing`,
            additionality_type: fertilizings[fertilizingId],
            tree_type: treeType.name,
            area: this.area.id,
            starting_dt: startingDt,
            starting_year: year,
            end_year: parseInt(year) + lengthYears,
            values: additionalities
          });


        }


        // CheckpointTreeTypes.postCheckpointTreeTypesFertilizing({
        //   id: 1,
        //   checkpoint_tree_type_id: 153,
        //   'tree-types-fertilizing_id': 1,
        // })
        //}

      } else if (additionality == "thinning") {


        // let fertilizings = (await Fertilizings.getFertilizings()).data


        const { value: date } = await Swal.fire({
          title: "Select additionality start date",
          input: "date",
          didOpen: () => {
            // const today = (new Date()).toISOString();
            // Swal.getInput().min = today.split("T")[0];
          }
        });

        if (!date) return;

        const startingDt = moment(date).format("YYYY-MM-DD");
        const defaultYear = parseInt(moment(date).format("YYYY")) + 1;

        const { value: year } = await Swal.fire({
          title: "Select additionality start date",
          input: "number",
          inputValue: defaultYear
        });

        if (!year) return;

        let areaLastCheckpointData = this.area.theoreticalStats || this.area.last_checkpoint?.theoretical_stats || this.area.last_checkpoint?.data?.theoreticalStats || [];

        console.log({ area: this.area });
        console.log({ areaLastCheckpointData });

        let thinningPercentFactor = 1 - (1000 / (this.area.qty / this.area.area_ha));
        let additionalThinningPercentFactor = 0.20;
        console.log({ thinningPercentFactor });
        console.log({ additionalThinningPercentFactor });
        console.log({ areaLastCheckpointData });


        let additionalities = [];
        for (const i in areaLastCheckpointData) {
         if (areaLastCheckpointData[i].year >= year) {

            console.log({ areaLastCheckpointDataINDEX: i });

            let baselineValue = (areaLastCheckpointData[i].theoreticalIncrement * thinningPercentFactor);
            additionalities.push({
              year: additionalities.length,
              value: baselineValue * additionalThinningPercentFactor,
              baseline_value: -baselineValue
            });
          }
        }


        Swal.fire({
          title: "Processing",
          html: "Wait a moment...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: () => !Swal.isLoading()
        });

        await Additionalities.postAdditionalities({
          priority: 2,
          type: "Thinning",
          area: this.area.id,
          starting_dt: startingDt,
          starting_year: year,
          end_year: parseInt(year) + areaLastCheckpointData.length,
          values: additionalities
        });


      } else if (additionality == "postponedFinalCut") {


        // let fertilizings = (await Fertilizings.getFertilizings()).data


        const { value: date } = await Swal.fire({
          title: "Select additionality start date",
          input: "date",
          didOpen: () => {
            // const today = (new Date()).toISOString();
            // Swal.getInput().min = today.split("T")[0];
          }
        });

        if (!date) return;

        const startingDt = moment(date).format("YYYY-MM-DD");
        const defaultYear = parseInt(moment(date).format("YYYY")) + 1;

        const { value: year } = await Swal.fire({
          title: "Select additionality start date",
          input: "number",
          inputValue: defaultYear
        });

        if (!year) return;

        let areaLastCheckpointData = this.area.theoreticalStats || this.area.last_checkpoint?.theoretical_stats || this.area.last_checkpoint?.data?.theoreticalStats || [];


        let additionalities = [];
        for (const i in areaLastCheckpointData) {

          if (areaLastCheckpointData[i].year >= year) {

            console.log({ areaLastCheckpointDataINDEX: i });

            additionalities.push({
              year: additionalities.length,
              value: areaLastCheckpointData[i].totalTonCCO2,
              baseline_value: -areaLastCheckpointData[i].totalTonCCO2
            });
          }
        }


        Swal.fire({
          title: "Processing",
          html: "Wait a moment...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: () => !Swal.isLoading()
        });

        await Additionalities.postAdditionalities({
          priority: 999,
          type: "Postponed final cut",
          area: this.area.id,
          starting_dt: startingDt,
          starting_year: year,
          end_year: parseInt(year) + areaLastCheckpointData.length,
          values: additionalities
        });


      }


        await this.loadArea();
        Swal.fire(
          "Done!",
          `Additionality successfully added`,
          "success"
        );
      } catch (err) {
        console.error("Error adding additionality:", err);
        Swal.fire(
          "Error",
          err.response?.data?.error?.message || err.message || "Could not add additionality",
          "error"
        );
      }
    },
    async withdraw() {


      if (this.balance) {
        Swal.fire({
          title: "Are you sure?",
          text: "You will transfer all the ZCO2 tokens from the forest wallet your connected wallet",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes"
        }).then(async (result) => {
          if (result.isConfirmed) {
            const res = await Swal.fire({
              title: "Enter the receiver wallet address",
              input: "text",
              inputLabel: "Wallet address",
              inputValue: this.getActiveAccount,
              showCancelButton: true,
              inputValidator: (value) => {
                if (!value) {
                  return "You need to write a wallet address!";
                }
              }
            });

            if (res.isConfirmed && res.value) {

              const amount = await Swal.fire({
                title: "Enter the amount of ZCO2",
                input: "number",
                inputLabel: "ZCO2 amount",
                inputValue: parseFloat(this.balance || 0).toFixed(4),
                showCancelButton: true,
                inputValidator: (value) => {
                  if (parseFloat(value) > parseFloat(this.balance)) {
                    return `Max amount is ${this.balance}`;
                  }
                }
              });


              if (amount.isConfirmed) {
                const normalizedAmount = typeof amount.value === 'string'
                  ? parseFloat(amount.value.replace(',', '.'))
                  : parseFloat(amount.value);

                if (!normalizedAmount || isNaN(normalizedAmount)) {
                  Swal.fire(
                    "Empty amount",
                    `Amount can not be empty`,
                    "error"
                  );
                  return;
                }


                Swal.fire({
                  title: "Processing",
                  html: "Waiting for confirmations",
                  didOpen: () => {
                    Swal.showLoading();
                  },
                  allowOutsideClick: () => !Swal.isLoading()
                });
                Area.withdraw(this.area.id, {
                  walletAddress: res.value,
                  amount: normalizedAmount
                }).then((data) => {
                  const txHash = data?.data?.hash || data?.hash;
                  const txLink = (this.config?.tx_url_template && txHash)
                    ? `<br><a href="${this.config.tx_url_template.replace("{tx}", txHash)}" target="_blank">Go to explorer</a>`
                    : "";
                  Swal.fire(
                    "Withdraw done successfully",
                    `You've successfully withdrawn your ZCO2 tokens.${txLink}`,
                    "success");

                  this.loadArea();

                }).catch((e) => {
                  console.log("e", e);
                  const errorMsg = e.response?.data?.error?.message 
                    || e.response?.data?.message 
                    || e.response?.data 
                    || e.message 
                    || "Withdrawal transaction failed";
                  Swal.fire(
                    "Error",
                    typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : String(errorMsg),
                    "error");
                });

              }

            }
          }
        });
      } else {
        Swal.fire(
          "No balance!",
          `This forest balance is empty`,
          "error"
        );
      }


      // }
    },
    textShort(txt) {
      return textShortener(txt);
    },
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY") : "";
    },
    openEmissionModal() {
      this.emissionModal = { show: true, date: null, concept: "", tonCo2: null, notes: "" };
    },
    async saveEmission(bvModalEvent) {
      if (!this.emissionModal.date || !this.emissionModal.concept || !this.emissionModal.tonCo2) {
        // keep the modal open so nothing typed is lost
        if (bvModalEvent) bvModalEvent.preventDefault();
        Swal.fire("Missing data", "Date, Concept and Ton CO2 are required", "warning");
        return;
      }
      try {
        await Emission.postEmissions({
          area: this.area.id,
          date: this.emissionModal.date,
          concept: this.emissionModal.concept,
          tonCo2: parseFloat(this.emissionModal.tonCo2),
          notes: this.emissionModal.notes
        });
        await this.loadArea();
      } catch (err) {
        Swal.fire("Error", err.message || "Could not save the emission", "error");
      }
    },
    async deleteEmission(emission) {
      const result = await Swal.fire({
        title: "Delete this emission?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete"
      });
      if (!result.isConfirmed) return;
      try {
        await Emission.delEmissions(emission.id);
        await this.loadArea();
      } catch (err) {
        Swal.fire("Error", err.message || "Could not delete the emission", "error");
      }
    }
  }
};
</script>

<style scoped>
.certificates-card {
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 16px;
}

.certificates-title {
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 10px;
}

.certificates-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cert-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
}

.cert-icon {
  width: 18px;
  text-align: center;
  color: var(--accent-green);
  flex-shrink: 0;
}

.cert-info {
  flex: 1;
  min-width: 0;
}

.cert-label {
  font-weight: 600;
  color: var(--text-heading);
  font-size: 0.9rem;
}

.cert-value {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.cert-value a {
  color: var(--text-muted);
}

.cert-link {
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--accent-green);
  white-space: nowrap;
}

.cert-link:hover {
  color: var(--accent-green);
  text-decoration: underline;
}

.cert-badge-wrap {
  display: flex;
  justify-content: center;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-subtle);
}

.cert-badge-img {
  max-width: 160px;
  max-height: 160px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid var(--border-subtle);
  background: #fff;
  padding: 6px;
}

/* Headings wrap gracefully instead of the default h4 size forcing an
   awkward mid-word break on narrower "data-chart" column widths. */
.stats-heading {
  font-size: 1.05rem;
  line-height: 1.3;
}

/* Tighter padding than the global .table default (which is tuned for the
   wide forest-list table) so these denser, many-column stat tables fit
   the "data-chart" column without needing horizontal scroll on common
   desktop widths. */
::v-deep .compact-table th,
::v-deep .compact-table td {
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
}

/* Marks "now" in the Theoretical (ex-ante) tables, which now include the
   full history — scrollTableToCurrentYear() lands the view here by default,
   this just makes that row visually obvious once it does. */
::v-deep .compact-table tr.current-year-row td {
  background-color: rgba(var(--accent-green-rgb), 0.14) !important;
  font-weight: 600;
  box-shadow: inset 0 1px 0 rgba(var(--accent-green-rgb), 0.4), inset 0 -1px 0 rgba(var(--accent-green-rgb), 0.4);
}

/* The map card's background stays whatever the theme gives it (untouched
   here) — only the inactive tab buttons change. Bootstrap's inactive
   .btn-outline-light is near-white text on a transparent background, which
   is unreadable in light mode until hovered. Giving them that same hover
   look (dark text, subtle gray fill) by default fixes that, without
   touching anything else. */
[data-theme="light"] .map-card-fill .btn-outline-light {
  color: #212529 !important;
  background-color: #f8f9fa !important;
  border-color: #f8f9fa !important;
}

.map-box {
  position: relative;
}

.map-expand-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.map-expand-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.map-modal-box {
  height: 70vh;
  min-height: 360px;
  position: relative;
}

.map-box-lg {
  position: relative;
  height: 280px;
}

.growth-chart-wrap {
  position: relative;
  height: 280px;
  flex: 1 1 auto;
  min-width: 0;
}

.growth-panel-loading {
  height: 280px;
}

/* Bootstrap's default column gutter (15px each side = 30px total) reads as
   a big empty gap between the map and the chart card — tighten it. */
.overview-row {
  margin-left: -8px;
  margin-right: -8px;
}

.overview-row > .overview-col-fill {
  padding-left: 8px;
  padding-right: 8px;
}

/* PC screens: let the map and the chart use all the vertical room the
   viewport actually has, instead of a fixed px height — there's no reason
   to leave blank space below them when the window is tall enough. Scoped
   to the lg breakpoint (matches the 2-column split below) so mobile/tablet
   keep the simple fixed-height fallback above. */
@media (min-width: 992px) {
  .overview-col-fill {
    height: calc(100vh - 280px);
    min-height: 300px;
    display: flex;
    flex-direction: column;
  }

  .map-card-fill,
  .chart-card-fill {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .map-box-lg,
  .growth-panel-loading {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
  }

  .chart-card-fill ::v-deep .tabs {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }

  .chart-card-fill ::v-deep .tab-content {
    flex: 1 1 auto;
    min-height: 0;
  }

  .chart-card-fill ::v-deep .tab-pane.active {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .chart-with-side {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  .growth-chart-wrap {
    height: 100%;
  }

  /* Additional/Base table panels: stretch to fill whatever room the tab
     pane has (the actual scroll cutoff comes from the JS-measured
     :sticky-header height, not from this — this just lets the mai-card +
     table stack fill the column instead of leaving blank space below). */
  .table-panel {
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* Chart / table + the Additionality-Emissions panel sitting beside it
   (right-hand lateral column) instead of stacked above it. Reused by the
   Chart tab and the Theoretical/Additional tab. */
.chart-with-side {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.min-w-0 {
  min-width: 0;
}

.additionality-side {
  flex: 0 0 200px;
  width: 200px;
  border-left: 1px solid var(--border-subtle);
  padding-left: 14px;
}

/* Additionality + Emissions compact strip (reused inside the Chart tab and
   the Theoretical/Additional tab) — a column of wrapping badge groups
   instead of a full card, so it costs far less space than the old sidebar
   card did. */
.additionality-strip {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.as-group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.82rem;
}

.as-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.as-label {
  font-weight: 700;
  color: var(--text-muted);
}

/* Small pill beside the section title instead of a full-width button sitting
   below the chips — keeps the add action next to what it adds to, and
   carries its own icon + label + background so it still reads as a button. */
.as-add-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-elevated);
  color: var(--text-main);
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.as-add-btn:hover {
  color: #fff;
  border-color: var(--accent-green);
  background: var(--accent-green);
}

.as-empty {
  line-height: 1.3;
}

/* Additionality/emissions cap their height instead of stretching the side
   panel indefinitely — a handful of chips fit without a scrollbar, many
   chips scroll vertically instead. */
.as-chip-scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 4px;
}

.as-chip-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.as-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  font-size: 0.8rem;
  text-align: left;
  color: var(--text-main);
  transition: border-color 0.15s ease;
}

.as-chip-scroll > .as-chip {
  flex-shrink: 0;
  width: 100%;
}

/* Per-tree-type rows nested under a single grouped additionality chip
   (e.g. "Wood ash fertilizing" applied to Pine/Spruce/Birch) so the type
   itself isn't repeated once per species. */
.as-subitem-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 8px;
}

.as-subitem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 0.68rem;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  padding: 3px 6px;
}

.as-subitem-remove {
  flex-shrink: 0;
  cursor: pointer;
  color: var(--text-muted);
  padding: 3px;
  border-radius: 50%;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.as-subitem-remove:hover {
  color: #fff;
  background: rgba(220, 53, 69, 0.7);
}

.as-chip:hover {
  border-color: var(--accent-green);
}

.as-chip-icon {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(var(--accent-green-rgb), 0.16);
  color: var(--accent-green);
  font-size: 0.72rem;
}

.as-chip--negative .as-chip-icon {
  background: rgba(224, 163, 55, 0.18);
  color: #e0a337;
}

.as-chip-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1 1 auto;
}

.as-chip-title {
  font-weight: 600;
  overflow-wrap: break-word;
}

.as-chip-sub {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.as-chip-sub--negative {
  color: #e0a337;
  font-weight: 600;
}

.as-chip-remove {
  flex-shrink: 0;
  cursor: pointer;
  color: var(--text-muted);
  padding: 5px;
  border-radius: 50%;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.as-chip-remove:hover {
  color: #fff;
  background: rgba(220, 53, 69, 0.7);
}

/* "Mean Annual Increase" stat, dressed up as a small highlight pill instead
   of a plain line of text — horizontal (label beside value) so it stays
   short, since the tables below need all the vertical room they can get. */
.mai-card {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  background: rgba(var(--accent-green-rgb), 0.08);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  padding: 5px 16px;
}

.mai-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}

.mai-scope {
  text-transform: none;
  letter-spacing: normal;
}

.mai-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--accent-green);
  white-space: nowrap;
}

/* Scan info button, overlaid near the top of the growth chart — clicking it
   opens a nicely formatted popup of the most recent Real (ex-post) scan,
   since the chart's yellow "Now" line roughly marks that point in time. */
.scan-info-btn {
  position: absolute;
  top: 6px;
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border: none;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 0.72rem;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.scan-info-btn:hover {
  background: rgba(0, 0, 0, 0.75);
}

.scan-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.scan-detail-card {
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 12px 14px;
}

.scan-detail-card h5 {
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.scan-detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.scan-detail-row strong {
  color: var(--text-main);
}

.scan-detail-total {
  display: flex;
  gap: 24px;
  font-size: 0.9rem;
  border-top: 1px solid var(--border-subtle);
  padding-top: 12px;
}

/* Trees / Splits tags and the tree stock window */
.purpose-pill {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  padding: 4px 9px;
  border-radius: 999px;
  margin-right: 6px;
  border: 1px solid transparent;
  white-space: nowrap;
  vertical-align: middle;
}

.purpose-trees {
  background: rgba(72, 187, 120, 0.16);
  color: #1f7a4c;
  border-color: rgba(72, 187, 120, 0.6);
}

.purpose-splits {
  background: rgba(66, 153, 225, 0.16);
  color: #2b6cb0;
  border-color: rgba(66, 153, 225, 0.6);
}

[data-theme="dark"] .purpose-trees {
  color: #7be0ae;
}

[data-theme="dark"] .purpose-splits {
  color: #86c4ff;
}

.purpose-card {
  height: 100%;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 12px 14px;
}

.purpose-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.purpose-card-total {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
}

.purpose-card-list {
  margin: 6px 0 0;
  padding-left: 1.1rem;
  font-size: 0.88rem;
}

.purpose-form {
  border-top: 1px solid var(--border-subtle);
  padding-top: 14px;
}

/* Header */
.area-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 8px 18px;
  margin-bottom: 8px;
}

.area-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.area-title-gps {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.area-title-badge {
  font-size: 0.75rem;
}

.area-country {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.area-header-stats {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.area-stat-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
}

.area-stat-value {
  font-weight: 600;
}

.area-withdraw-btn {
  white-space: nowrap;
}

/* Shorter screens below the lg breakpoint (map/chart stacked, not filling
   the viewport height): shrink them so the whole page still fits without
   vertical scroll. Above lg, the calc(100vh - ...) fill rule already
   adapts to the viewport's height on its own. */
@media (max-height: 820px) and (max-width: 991.98px) {
  .map-box-lg,
  .growth-chart-wrap,
  .growth-panel-loading {
    height: 200px;
  }
}

/* Full-page loading state: a little sprout that grows in once, then sways
   gently in place — instead of a generic spinner, matching the forest/
   plantation theme used throughout this page (map, additionality "leaf"
   icons, etc). It settles into a held, idle pose quickly (well under a
   second) rather than a slow multi-second grow/fade loop, since real data
   usually arrives before a longer loop would ever complete a full cycle. */
.area-loading {
  flex-direction: column;
  gap: 14px;
}

.plant-grow {
  width: 104px;
  height: 124px;
  overflow: visible;
}

.plant-soil {
  fill: var(--border-subtle);
  opacity: 0.8;
}

.plant-sway {
  transform-box: fill-box;
  transform-origin: 50% 100%;
  animation: plant-sway 2.4s ease-in-out 0.75s infinite;
}

.plant-stem {
  fill: none;
  stroke: var(--accent-green);
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: plant-stem-grow 0.45s ease-out forwards;
}

.plant-leaf {
  opacity: 0;
  transform: scale(0);
  transform-box: fill-box;
  animation: plant-pop 0.3s ease-out forwards;
}

.plant-leaf-left {
  fill: var(--accent-green);
  transform-origin: 100% 100%;
  animation-delay: 0.3s;
}

.plant-leaf-right {
  fill: var(--accent-green-light);
  transform-origin: 0% 100%;
  animation-delay: 0.4s;
}

.plant-bud {
  fill: var(--accent-green-light);
  opacity: 0;
  transform: scale(0);
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: plant-pop 0.25s ease-out 0.55s forwards;
}

.area-loading-text {
  color: var(--text-muted);
  font-size: 0.9rem;
  letter-spacing: 0.02em;
}

@keyframes plant-stem-grow {
  to { stroke-dashoffset: 0; }
}

@keyframes plant-pop {
  0% { opacity: 0; transform: scale(0); }
  70% { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes plant-sway {
  0%, 100% { transform: rotate(-2.5deg); }
  50% { transform: rotate(2.5deg); }
}
</style>
